import {
  cacheAfterPlay,
  clearCacheEntry,
  evictStale,
  getLocalUri,
  initSignageCache,
  queueSignageAssets,
} from "./localAssetCache";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  breakAt,
  formatDuration,
  nextBreakInSeconds,
  playheadAt,
} from "@shared/signage";
import type { SignageTimeline, SignageItem } from "@shared/types/signage";
import {
  type ChannelLogoPlacement,
  DEFAULT_CHANNEL_LOGO_OFFSET_X,
  DEFAULT_CHANNEL_LOGO_OFFSET_Y,
  DEFAULT_CHANNEL_LOGO_WIDTH,
} from "@shared/types/foyer";
import ChannelLogoOverlay from "./ChannelLogoOverlay";
import { bridge } from "../lib/foyerBridge";
import { reportSignagePlayback } from "../lib/signagePlayback";
import { trackContentPlay } from "../lib/engagement";
import { signagePlayEvent } from "./signageProofOfPlay";
import { avplayAvailable } from "./avplay";
import AvPlayLayer, { AvPlaySurface } from "./AvPlayLayer";
import { useVistarAds, withAdBreaks } from "./vistarAds";
import TemplateSlide from "./TemplateSlide";
import { clearWebFrames } from "./webFrameHost";
import { iframeSandbox } from "./iframeSandbox";
import {
  resolveItemPlayback,
  type PlaybackCaller,
  type ResolvedItemPlayback,
} from "./playbackLicence";

/**
 * The signage surface - a common-area screen playing its scheduled loop.
 *
 * Deliberately thin. Everything hard (which playlist is on at this hour, which
 * item within it, how far in) is answered by the SAME resolver the server and
 * the CMS use, so a lobby screen cannot drift from what an operator previewed.
 * This component only fetches, renders, and advances.
 *
 * Two properties matter more than features on a wall nobody is watching:
 *
 *  - it must never go black because the network hiccuped. The last good
 *    timeline is kept and keeps playing; a failed poll is not an event.
 *  - it must recover without a human. There is no error screen and no button:
 *    a bad item is skipped, a bad poll is retried on the next tick.
 *
 * Which is right on a wall and useless to the person asked why the wall is
 * black - idle, unauthorised, offline and every-asset-broken all look
 * identical. So the surface keeps a `status` it never shows in the room and
 * `diagnostics` renders in the CMS preview, where a caption costs nothing and
 * saves a site visit.
 */

/** How often to re-poll the schedule. Matches the legacy player's cadence. */
const POLL_MS = 30_000;
/**
 * How many times to retry `avplayAvailable()` before settling for `false`,
 * and how long to wait between attempts - same budget as the analogous
 * `webapis.productinfo` settle-time race fix (`SERIAL_READ_RETRY_ATTEMPTS`/
 * `SERIAL_READ_RETRY_DELAY_MS`, `src/tv/App.tsx`). See the `nativePlayer`
 * doc comment below for why this exists.
 */
const AVPLAY_SETTLE_RETRY_ATTEMPTS = 8;
const AVPLAY_SETTLE_RETRY_DELAY_MS = 500;
/**
 * How long a video's `currentTime` may stand still before the watchdog acts.
 *
 * Generous on purpose: a panel fetching the next few seconds over a hotel's
 * uplink can legitimately sit still for a moment, and remounting on a normal
 * rebuffer would turn a hitch into a restart. Nudge at 4s, rebuild at 12s.
 */
const STALL_PLAY_MS = 4_000;
/**
 * Watchdog cadence. 500ms rather than 1s so the end-of-clip case above is
 * caught within about half a second; the body is two number comparisons, which
 * is nothing even on a panel.
 */
const WATCHDOG_MS = 500;
/**
 * DIAGNOSTIC ONLY (2026-10-12). How long the ACTIVE video may sit paused or
 * detached before the surface says so once.
 *
 * The watchdog deliberately ignores such an element (see `isIdleElement`), so
 * a layer that is shown but never starts playing - black, logo showing -
 * produces no `STALLED` line at all. A dev soak on a Samsung QB24C went black
 * with exactly that signature: the loop kept "showing" items every few
 * minutes, no stall, no failure, no error. This line makes that state visible
 * in the shipped log without changing what the watchdog does about it. It also
 * fires for a legitimate image promo (the last video is paused while a still
 * is up), which is why it only describes and never acts.
 */
const IDLE_NOTE_MS = 8_000;

/**
 * MEASURED 2026-10-12 on a QB24C (two stuck cold starts, logged): a <video>
 * that is NOT paused, sits at `readyState` <= 1 with `networkState` idle and
 * data buffered AHEAD of its playhead, and has not moved, is a wedged decoder.
 * Healthy cold starts on the same panel sat at that exact state (rs=1, t=2.08)
 * for up to ~12s before moving, so the threshold has to clear that with room
 * to spare. 103s of it was observed (`Stalled - stuck 103s at 2.1s`).
 *
 * The 2026-08-13 revert removed remounts that fired on a 500ms interval and
 * mistook healthy spin-ups for wedges (`recovered` reached 84). This one is
 * gated on the signature above, waits WEDGE_REMOUNT_MS, and gives up after
 * WEDGE_REMOUNT_MAX per stuck episode, so it cannot become a remount per lap.
 */
/**
 * How long a video must have had the screen to itself before the NEXT video is
 * allowed to start warming up behind it.
 *
 * 2026-10-15, QB24C: a clip started warm (`show readyState=4`, `play()
 * resolved`) and about 3 s later its element was back at `readyState 0`, then
 * `STALLED ... no-source` and `manifest never started`: 33 s of black at an
 * ordinary item change. The only thing that happened in between was the
 * pre-roll twin for the NEXT item being mounted ~1.4 s after the switch - a
 * second Shaka/MediaSource instance spinning up while the first was still
 * settling, on a panel that has one usable decoder. Warming the twin a few
 * seconds later costs nothing (a clip lasts minutes) and keeps the two start-ups
 * from overlapping.
 */
const PREROLL_SETTLE_MS = 10_000;

/**
 * Does this item have to ask the backend for a playback (URL + licence) before
 * it may be drawn?
 *
 * Only a catalogue title the timeline has NOT declared clear. The resolver
 * writes `encrypted: false` for a free title (a free channel, a venue film), and
 * for that one the timeline's own URL is the one to play: there is no licence to
 * wait for, so there is no black gate, no second player start-up when the
 * "resolved" URL arrives, and nothing that tears a warm layer down. Absent
 * (`undefined`, an older server's timeline) or `true` keeps the original
 * behaviour exactly - black rather than the clear rendition of a protected
 * title if its licence is refused.
 */
const needsLicence = (item: SignageItem): boolean =>
  !!item.contentAssetId && item.encrypted !== false;

const WEDGE_REMOUNT_MS = 30_000;
const WEDGE_REMOUNT_MAX = 3;
/**
 * How long `useManifestPlayback`'s attach/configure/load chain may run with
 * no result at all - success or failure - before it is treated as one.
 *
 * Added after the 2026-09-17/18 hasselbacken incident: the chain had no
 * timeout of any kind, so a hung `attach()`/`load()` (network stall, DNS
 * issue, a CDN request that never settles) left the element at
 * `readyState=0` for hours with nothing ever reported. Sized to match
 * `nenda-autoplayer`'s own `ShakaPlayer.tsx` (`SHAKA_STARTUP_FAIL_MS`).
 */
const STARTUP_WATCHDOG_MS = 30_000;

/**
 * How many times the manifest chain is rebuilt from scratch after the startup
 * watchdog fires, per url. Measured 2026-10-12 on a QB24C: the 28th transition
 * of a run hung in attach/load, the watchdog reported "manifest never started
 * within 30s", and the element then sat at `ready nothing · net no-source ·
 * buffered none` for 104s+ because reporting was all the watchdog did. Cold
 * starts on this panel succeed most of the time, so a fresh attempt is the
 * cheapest correct response; the cap stops a genuinely dead URL from looping.
 */
const STARTUP_RETRY_MAX = 2;
/**
 * A 1x1 black PNG, used as every clip's poster.
 *
 * Without a poster an Android WebView paints its OWN placeholder into the
 * element until the first frame decodes: a low-res play glyph on a grey field.
 * On a phone that reads as "tap to play"; on a lobby wall it is a broken-looking
 * grey rectangle, shown on every load, every loop restart and every watchdog
 * remount. A black poster is the same colour as the surface behind it, so the
 * clip simply appears out of black.
 *
 * Inline rather than a file: it must be there before any network request, and
 * an asset that 404s would put the placeholder straight back.
 */
const BLACK_POSTER =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
/** Horizon requested. Long enough that a poll failure is invisible for hours. */
const HORIZON_HOURS = 6;
/**
 * How long a failed item stays excluded before `pickPlayable` gives it
 * another chance.
 *
 * CONTENT-29 (2026-09-15): a screen reported "6 of 75 items failing" and
 * separately "stuck on item 1, never advancing" - and a full scan of the
 * catalogue (existence, DASH manifest, encryption, ABR ladder) found every
 * item in both channels genuinely healthy. `dead` never expiring turned out
 * to be the actual bug: ANY one-off failure (a CDN hiccup, a momentarily
 * exhausted decoder pool, a slow network moment) exiled a perfectly good item
 * for the rest of the panel's uptime - which on a fielded screen is days, not
 * minutes - and enough of those compounding is exactly what strands a
 * playlist on whichever early item happened to survive.
 *
 * Short enough that a genuinely broken file is not shown again within the
 * same lap of either measured playlist here (23.5 / 37.5 min); long enough
 * that it is not retried on every pass through a cascading `pickPlayable`
 * walk. Not a hardware measurement - a judgement call, tune it if a real
 * failure rate ever gets measured. The cost of being wrong in either
 * direction is small: too short retries a dead file more often (one more
 * failed load, same as today's behaviour repeated); too long is today's bug,
 * just with a ceiling instead of forever.
 */
export const DEAD_RETRY_MS = 5 * 60_000;

/**
 * Who is asking, and therefore which endpoint answers.
 *
 * A panel has exactly one credential - its screen token - and asks the
 * screen-scoped route. The CMS preview has no screen token and never will
 * (tokens are minted at pairing, for a device); it is authenticated as the
 * operator by the shared `.nenda.com` cookie and asks the premise-scoped
 * route. Same resolver behind both, so the preview cannot flatter the wall.
 */
export type SignageSource =
  | { kind: "screen"; token: string }
  | { kind: "operator"; premiseId: string };

/** Why nothing is on screen. Never rendered in a room - see `diagnostics`. */
type PollStatus =
  | { state: "loading" }
  | { state: "ok"; at: number }
  | { state: "http"; code: number; at: number; detail?: string }
  | { state: "offline"; at: number };

export interface SignageSurfaceProps {
  screenId: string;
  source: SignageSource;
  apiBase?: string;
  /**
   * Scrub to an instant other than now (ISO). Forwarded to the operator route,
   * which resolves the timeline AT that moment, and used as the playhead clock
   * unless `now` is given explicitly - so "what runs Sunday 08:00?" is a URL.
   * Ignored by the screen route: a panel plays the present.
   */
  at?: string;
  /** Injectable for tests. Defaults to `at`, then to the real clock. */
  now?: () => Date;
  /** Explain an empty screen instead of just being black. Preview only. */
  diagnostics?: boolean;
  /**
   * Where an effect channel's logo sits. Absent → the shared defaults - this
   * happens on the operator preview route, which runs before any
   * `PremiseConfig` fetch and so has no customised setting to hand down.
   */
  logoPlacement?: ChannelLogoPlacement;
}

/**
 * One card per KIND of problem, not one per affected title.
 *
 * A catalogue outage drops every item in the loop, and the surface drew a
 * warning for each: 355 identical cards, filling a television, in place of the
 * one sentence that would have explained it. A wall covered in the same
 * paragraph tells an operator less than a single copy of it does, and it buries
 * any OTHER warning - a wrong timezone, a missing template - underneath.
 *
 * Grouped by `code` alone rather than by message, so a hundred titles from one
 * playlist collapse together even though each message names its own title. The
 * first message is kept in full and the rest are counted: the count is the part
 * that says how bad it is.
 */
function collapseWarnings(
  warnings: readonly { code: string; value?: string; message: string }[]
): { code: string; value: string; message: string; repeats: number }[] {
  const byCode = new Map<
    string,
    { code: string; value: string; message: string; repeats: number }
  >();
  for (const w of warnings) {
    const found = byCode.get(w.code);
    if (found) found.repeats += 1;
    else
      byCode.set(w.code, {
        code: w.code,
        value: w.value ?? "",
        message: w.message,
        repeats: 1,
      });
  }
  return [...byCode.values()];
}

export default function SignageSurface({
  screenId,
  source,
  apiBase = "",
  at,
  now,
  diagnostics = false,
  logoPlacement,
}: SignageSurfaceProps) {
  const [timeline, setTimeline] = useState<SignageTimeline | null>(null);
  // The point of the tick is the re-render, which re-reads the clock and
  // moves the playhead. Its value is read only by the ad hook, as a 1 Hz beat.
  const [tick, setTick] = useState(0);
  const [status, setStatus] = useState<PollStatus>({ state: "loading" });
  // Items that failed to load, id -> when. Skipped rather than shown as a
  // broken frame - one missing file must not park the screen on a grey box
  // forever. Keyed with a timestamp, not just membership: entries expire
  // after DEAD_RETRY_MS (see its comment) rather than exiling an item for the
  // rest of the panel's uptime over one transient failure.
  const dead = useRef<Map<string, number>>(new Map());
  // Bumped (not sized) on every markDead call, purely to force an immediate
  // re-render rather than waiting for the next 1Hz tick to notice. The actual
  // "how many are dead right now" is `currentlyDead.size` below, recomputed
  // fresh every render against the clock - it can also SHRINK between
  // renders as entries age out, which a plain size-tracking counter could not
  // reflect.
  const [, setDeadVersion] = useState(0);
  /**
   * Whether a campaign break owned the wall as of the LAST commit, or `null`
   * before the first one. Read during render (to tell a background<->break
   * EDGE from ordinary rotation - see the layer-pair computation below),
   * written in a post-commit effect rather than during render itself:
   * mutating a ref mid-render is the exact class of bug this file's own
   * StrictMode/teardown-race history warns about (see
   * [[signage-playback-surface]], the 2026-08-27 correction) - render can run
   * more than once without committing, so only what actually committed may
   * be trusted as "what was on screen last". `null`, not `false`, for
   * "unknown yet" - the FIRST commit of a session must never read as an edge,
   * or the very first clip shown after a boot (the moment pre-rolling
   * matters most) would lose its pre-roll to this mitigation.
   */
  const lastOnBreak = useRef<boolean | null>(null);
  /**
   * The loop's video that was on screen when an IMAGE break took the wall.
   *
   * Kept mounted (hidden, paused) for the length of the break so the break
   * ends with a warm `play()` instead of a cold remount. 2026-10-15, QB24C:
   * every campaign ended with the loop's video torn down and rebuilt - restart
   * from 0:00, stutter, and on a bad day a wedged frame or black with the
   * channel logo. An image needs no decoder, so holding one video costs
   * nothing the teardown was saving. Cleared the moment the break is over.
   */
  const pausedLoop = useRef<SignageItem | null>(null);
  /**
   * Staging area for the above: written (late in render, once `onBreak` is
   * known) but never READ during render, so writing to it mid-render cannot
   * make render itself non-idempotent - only the effect below, which runs
   * strictly after a real commit, ever reads it.
   */
  const pendingOnBreak = useRef<boolean | null>(null);
  // Kept-alive web frames (`webFrameHost.ts`) live outside React, so nothing
  // else removes them when the wall itself goes away.
  useEffect(() => () => clearWebFrames(), []);

  useEffect(() => {
    if (pendingOnBreak.current !== null) {
      // DIAGNOSTIC ONLY: say when a campaign break takes or gives back the
      // wall. A black wall after a break is otherwise indistinguishable, in
      // the shipped log, from one that never had a break at all.
      if (
        lastOnBreak.current !== null &&
        lastOnBreak.current !== pendingOnBreak.current
      ) {
        console.warn(
          `[signage-loop] ${pendingOnBreak.current ? "BREAK START" : "BREAK END"} · item=${pendingItemId.current ?? "none"}`
        );
      }
      lastOnBreak.current = pendingOnBreak.current;
      pendingOnBreak.current = null;
    }
  });
  /**
   * The active content item's id as of the LAST commit, mirroring
   * `lastOnBreak` exactly (same staging pattern, same reasoning) - so an
   * ORDINARY rotation step can be told apart from "still the same item,
   * re-rendered for an unrelated reason" the same way a background<->break
   * EDGE already is below.
   *
   * 2026-09-16: added once ordinary rotation started genuinely completing
   * real, long DASH-manifest items for the first time (the natural-end
   * advancement fix, CONTENT-29 continued) - a case this surface had never
   * actually exercised before, because the wall-clock-estimate bug that fix
   * closed had kept every such playlist pinned near its first item. Once it
   * could, hasselbacken/301 (Philips) went black at real transitions,
   * reproduced twice: `useManifestPlayback` is enabled for BOTH layers of a
   * manifest item regardless of `active` (see its call site) - the pre-roll
   * layer already holds a live Shaka/MediaSource instance by design, so an
   * ordinary step creates a NEW one for the incoming `upcoming` at the same
   * commit the OUTGOING layer's `void player?.destroy()` fires,
   * unawaited. Exactly the acquire/release overlap the break-edge mitigation
   * below was built for, just never reachable at an ordinary step before.
   */
  const lastItemId = useRef<string | null>(null);
  const pendingItemId = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (pendingItemId.current !== undefined) {
      lastItemId.current = pendingItemId.current;
      pendingItemId.current = undefined;
    }
  });
  /**
   * CONTENT-29, continued: which content-layer position the surface is
   * actually holding, for a VIDEO item with no declared duration.
   *
   * `durationSeconds: 0` means "play to its natural end" - only the player
   * knows how long that really is, so `playheadAt`'s wall-clock index for
   * such an item is an ESTIMATE (see its own doc comment), never a real
   * schedule. Letting the wall clock keep driving item SELECTION off that
   * estimate anyway was the actual bug: the estimate reset every poll, so a
   * long real clip got force-cut and re-cut before it could ever finish,
   * and the whole playlist could never progress past whichever early item
   * the estimate kept landing back on.
   *
   * `null` (not tracking) means "the wall clock is authoritative here" -
   * true for every item with a real, operator-declared duration, and true
   * the moment the wall clock names a DIFFERENT playlist (a genuine
   * schedule change - a new block began, an override started or ended).
   * Set ONLY for a video item with `durationSeconds === 0`; cleared the
   * moment the (locally-advanced) position stops being one.
   *
   * `null` vs `undefined` mirrors `lastOnBreak`/`pendingOnBreak` above:
   * `undefined` on `pendingHeldIndex` means "no change to commit this
   * render", so it must be distinguishable from "commit a real null".
   */
  const heldIndex = useRef<{ playlistId: string; index: number } | null>(null);
  const pendingHeldIndex = useRef<
    { playlistId: string; index: number } | null | undefined
  >(undefined);
  useEffect(() => {
    if (pendingHeldIndex.current !== undefined) {
      heldIndex.current = pendingHeldIndex.current;
      pendingHeldIndex.current = undefined;
    }
  });
  // Forces a re-render after `advancePastNaturalEnd` mutates `heldIndex`
  // directly from the (real, post-commit) `onEnded` event - mutating a ref
  // alone does not schedule one.
  const [, forceAfterNaturalEnd] = useState(0);
  // Bumped only to force a fresh <video> element after a stall. Deliberately
  // NOT a per-pass counter: including the loop iteration in the key would
  // re-download the clip on every lap.
  /**
   * Zero except after a CONFIRMED decoder wedge (WEDGE_REMOUNT_MS, added
   * 2026-10-12). The interval-driven remount paths that incremented it were removed on
   * 2026-08-13 (see the watchdog below), so `recovered` now reports 0 for
   * every screen - which is the honest number, not a missing one. The state
   * stays because the layer keys read it and because a future recovery
   * mechanism, if one is ever justified by a measurement, belongs here.
   */
  const [nudge, setNudge] = useState(0);
  /**
   * Does this panel have a native player, and therefore is `<video>` off the
   * table entirely?
   *
   * Decided (at most) ONCE, and only ever false -> true. `webapis.avplay`
   * neither appears nor disappears during a session, and re-reading it per
   * render would make the choice of player something that can change
   * underneath a clip that is already playing - which on a single-surface
   * API means losing the decoder. That invariant is preserved below: once
   * this becomes `true` it is never set back to `false`, and it never flips
   * again after that first `true`.
   *
   * The reason this branch exists at all: a `<video src>` on the Samsung panels
   * we ship to fetches every byte and then declines to decode them. Measured on
   * hardware across three encodes, down to a 1280x720 Constrained Baseline clip
   * with no audio - all three parked at 0.1s, buffer full, network idle. See
   * `avplay.ts`.
   *
   * 2026-09-21 (hasselbacken/302, live hardware capture): a SINGLE
   * synchronous read at the very first render lost the exact same
   * settle-time race already found and fixed for `webapis.productinfo` in
   * the pairing/reattach path (`hasNativeIdentitySurface()` +
   * `reattachBySerial()`'s internal retry, `src/tv/App.tsx`, commit
   * `47fd2ca`) - `webapis.avplay.open` can still be initialising for a beat
   * right after a genuine hardware reboot, since a power cycle
   * re-initialises the whole TV OS under the widget, not just the widget.
   * Sampled at that exact instant, `avplayAvailable()` read `false`, and
   * because the read was a one-shot `useState` initializer, EVERY item for
   * the rest of that boot's session was permanently routed to `<video>` -
   * the one path proven above to never decode a single frame on this
   * hardware. In-app console capture confirmed it exactly: `[signage-loop]
   * show · readyState=0 net=0 t=0.00` at the first item, then total silence
   * from every AVPlay listener (`onbufferingstart`/`onerror`/
   * `onstreamcompleted` never fired once) for the following five minutes -
   * not a decode error, a player that was never even asked to try.
   *
   * Retried below for a short, bounded settle window (same shape and same
   * budget as the productinfo fix) before the first real decision is taken.
   * A device that genuinely has no AVPlay (Philips, a browser) still
   * settles to `false` and stays there - this only moves WHEN the one-shot
   * decision is made, it does not weaken it.
   */
  const [nativePlayer, setNativePlayer] = useState(() => avplayAvailable());
  useEffect(() => {
    if (nativePlayer) return;
    let cancelled = false;
    let attempts = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tryAgain = () => {
      if (cancelled) return;
      attempts += 1;
      if (avplayAvailable()) {
        setNativePlayer(true);
        return;
      }
      if (attempts >= AVPLAY_SETTLE_RETRY_ATTEMPTS) return;
      timer = setTimeout(tryAgain, AVPLAY_SETTLE_RETRY_DELAY_MS);
    };
    timer = setTimeout(tryAgain, AVPLAY_SETTLE_RETRY_DELAY_MS);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // Deliberately mount-once: this is the one-shot decision's own settle
    // window, not a per-render check - see the doc comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Stable identity: this lands in a layer effect's dependency list, and a new
  // function every render would re-run play() on every 1Hz tick.
  const adoptActiveVideo = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
  }, []);
  // The element's own account of a stall, for the caption and the heartbeat.
  // Null while playback is healthy.
  const [mediaState, setMediaState] = useState<string | null>(null);
  /**
   * Why a manifest never loaded, if that is what happened.
   *
   * A REF, not state: the stall watchdog reads it from inside a 500ms interval
   * whose effect must not be re-created on every failure, and a stale closure
   * here would report the previous item's reason against the current one.
   *
   * Kept beside `mediaState` rather than folded into it because the two are
   * different claims. `mediaState` is the ELEMENT's account of itself, read off
   * `readyState`/`networkState`/`buffered`. This is the PLAYER's account of why
   * it never got that far - and it is the half that was missing when a Shaka
   * failure reported as `ready nothing · net empty · buffered none`, the
   * documented signature of an entirely different bug.
   */
  const manifestError = useRef<string | null>(null);
  const [manifestReason, setManifestReason] = useState<string | null>(null);
  const noteManifestError = useCallback((reason: string | null) => {
    manifestError.current = reason;
    setManifestReason(reason);
  }, []);
  /**
   * Which key system the CDM actually chose, for the heartbeat.
   *
   * The player offers every system it can license and the DEVICE picks, so a
   * stream that plays no longer says which one played it - and on a panel
   * bolted to a wall there is no console to ask.
   */
  const [keySystem, setKeySystem] = useState<string | null>(null);
  /** A pre-rolling layer's failure is its own business until its turn comes. */
  const noteNothing = useCallback(() => {}, []);
  const noteNoKeySystem = useCallback(() => {}, []);
  const stall = useRef<{ time: number; since: number }>({ time: -1, since: 0 });
  /**
   * How far this panel's clock is from the server's, in milliseconds.
   *
   * A signage schedule is resolved ENTIRELY from wall-clock: which interval
   * contains now, how far into the loop we are. So the resolver is only as
   * correct as the clock it is handed, and a television is a device with a
   * cheap RTC, no NTP guarantee, and a habit of coming back from a power cut
   * believing it is January.
   *
   * A panel that drifts past the end of its last interval renders "Nothing
   * scheduled right now" - which is ALSO what a correctly-configured screen
   * outside its hours says, and what a screen with no schedule at all says.
   * Identical words, three causes, and the operator's schedule was right all
   * along.
   *
   * So take the offset from the server on every poll and resolve against the
   * SERVER's clock instead. Self-correcting every 30 seconds, exact to within
   * the request latency (milliseconds, against a drift measured in minutes),
   * and it removes the panel's clock from the problem rather than tolerating
   * it. `signage.ts`'s skew grace stays as the fallback for a response with no
   * usable `generatedAt`.
   */
  const skewMs = useRef(0);
  const [skewSeconds, setSkewSeconds] = useState(0);
  const clock =
    now ??
    (at
      ? () => new Date(at)
      : // The correction applies ONLY to the real clock. An explicit `now` or
        // `at` is someone asking "what runs at this instant", and shifting it
        // underneath them would answer a different question.
        () => new Date(Date.now() + skewMs.current));

  const token = source.kind === "screen" ? source.token : null;
  const premiseId = source.kind === "operator" ? source.premiseId : null;

  /* -- poll ------------------------------------------------------------- */

  useEffect(() => {
    let alive = true;
    const url = token
      ? `${apiBase}/api/signage/v1/screens/${encodeURIComponent(
          screenId
        )}/signage?hours=${HORIZON_HOURS}`
      : `${apiBase}/api/cms/v1/premise/${encodeURIComponent(
          premiseId ?? ""
        )}/signage/screens/${encodeURIComponent(
          screenId
        )}/timeline?hours=${HORIZON_HOURS}${
          at ? `&at=${encodeURIComponent(at)}` : ""
        }`;
    const pull = async () => {
      try {
        const sentAt = Date.now();
        const res = await fetch(url, {
          headers: token ? { "X-Screen-Token": token } : {},
          // The operator route is cookie-authed. The cookie is scoped to the
          // zone, so it travels to the TV host on a same-origin request; being
          // explicit means a future absolute `apiBase` doesn't silently drop it.
          credentials: "include",
        });
        if (!alive) return;
        if (!res.ok) {
          // Keep playing what we have - but remember why, for the preview.
          // The body carries the server's own sentence (Fastify puts the error
          // message on a 500), and repeating it verbatim is the difference
          // between "500" and "Invalid time zone specified: Stockholm".
          setStatus({
            state: "http",
            code: res.status,
            at: Date.now(),
            detail: await errorDetail(res),
          });
          return;
        }
        const next = (await res.json()) as SignageTimeline;
        if (!alive) return;
        setStatus({ state: "ok", at: Date.now() });
        // Re-measured on EVERY poll, including the ones that change nothing -
        // drift is continuous and the revision check below deliberately drops
        // unchanged timelines, so tying the correction to a content change
        // would let it go stale exactly when the schedule is stable.
        const generatedAt = Date.parse(next.generatedAt);
        if (Number.isFinite(generatedAt)) {
          const prevSkewMs = skewMs.current;
          skewMs.current = generatedAt - Date.now();
          // DIAGNOSTIC ONLY (2026-10-12). The wall's clock is the server's
          // `generatedAt` taken at the instant the response ARRIVES, so a slow
          // poll leaves it behind by roughly the response time until the next
          // one lands. Break windows are exact instants on that clock: if it
          // jumps by seconds, a campaign can start and end on the screen in a
          // blink while the schedule itself never changed. Say when it does.
          const latencyMs = Date.now() - sentAt;
          if (
            latencyMs >= 2_000 ||
            Math.abs(skewMs.current - prevSkewMs) >= 2_000
          ) {
            console.warn(
              `[signage-loop] poll latency=${latencyMs}ms clock skew ${(prevSkewMs / 1000).toFixed(1)}s -> ${(skewMs.current / 1000).toFixed(1)}s`
            );
          }
          // Whole seconds, and only when it MOVES: this drives a render, and a
          // sub-second wobble on every 30s poll would be a re-render for noise.
          const whole = Math.round(skewMs.current / 1000);
          setSkewSeconds((cur) => (cur === whole ? cur : whole));
        }
        // Only re-render when something actually changed. The revision hash
        // covers durations and mute, not just asset ids, so a duration-only
        // edit does reach the screen.
        setTimeline((cur) => (cur?.revision === next.revision ? cur : next));
      } catch {
        // Offline. The last good timeline stays on screen.
        if (alive) setStatus({ state: "offline", at: Date.now() });
      }
    };
    void pull();
    const id = window.setInterval(pull, POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [screenId, token, premiseId, apiBase, at]);

  /* -- advance ---------------------------------------------------------- */

  // A 1Hz tick drives the playhead. Cheap, and it means the surface
  // self-corrects after a stall instead of drifting from wall-clock forever.
  //
  // The same tick is the video watchdog. A wall has nobody to press play: a
  // decoder that wedges mid-clip - which is a real event on the panels we ship
  // to, not a theoretical one - looks exactly like a frozen picture and stays
  // that way until somebody drives to the site. So compare `currentTime`
  // against the last tick, and escalate: nudge `play()` first, remount the
  // element only if that does not take.
  useEffect(() => {
    // The watchdog runs at WATCHDOG_MS so the end-of-clip case is caught
    // quickly, but the PLAYHEAD still advances once a second - it is a modulo
    // over wall-clock and gains nothing from being recomputed twice as often,
    // and re-rendering the surface at 2Hz for no reason is exactly the kind of
    // cost a panel cannot spare.
    let lastTickAt = 0;
    // DIAGNOSTIC ONLY - see IDLE_NOTE_MS. Which element has been idle, since
    // when, and whether that has been said yet.
    let idleEl: HTMLVideoElement | null = null;
    let idleSince = 0;
    let idleNoted = false;
    // Remounts spent on the CURRENT stuck episode; cleared the moment the
    // playhead moves again.
    let wedgeRemounts = 0;
    const id = window.setInterval(() => {
      const at = Date.now();
      if (at - lastTickAt >= 1000) {
        lastTickAt = at;
        setTick((t) => t + 1);
      }
      const v = videoRef.current;
      if (!v) return;
      /*
       * ⚠️ AN ELEMENT THAT IS NOT PLAYING IS NOT STALLING.
       *
       * `onActiveVideo` is called when a layer becomes active and never hands
       * back null, so this ref keeps pointing at the last VIDEO the loop
       * showed. The moment something that is not a video takes the wall - a
       * campaign break with an image promo, a slide, a web page - that
       * element's playhead legitimately stops, and four seconds later the
       * watchdog reported `video stalled` about a clip nobody could see.
       *
       * Reported as red, on screen, immediately before a promotion played
       * perfectly. Measured 2026-08-31.
       *
       * Guarding here rather than tightening the registration protocol: the
       * ordering between one layer releasing and the next adopting is a React
       * effect-order detail across siblings, and a watchdog that depends on it
       * is one refactor from lying again. `paused` and `isConnected` are facts
       * about the element itself. A genuine stall is never paused - the
       * element is trying and failing - so this cannot mask the thing the
       * watchdog exists for.
       */
      if (isIdleElement(v)) {
        stall.current = { time: v.currentTime, since: at };
        // Describe, never act: the guard above is right about an image promo,
        // but it is also exactly what hides a video that was shown and never
        // started. Say it once per element, with the facts that tell them apart.
        if (idleEl !== v) {
          idleEl = v;
          idleSince = at;
          idleNoted = false;
        } else if (!idleNoted && at - idleSince >= IDLE_NOTE_MS) {
          idleNoted = true;
          console.warn(
            `[signage-loop] ACTIVE VIDEO IDLE ${IDLE_NOTE_MS / 1000}s (watchdog ignores this; may be an image promo) · ${layerState(v)}`
          );
        }
        return;
      }
      idleEl = null;
      if (v.currentTime !== stall.current.time) {
        // Moving. Reset the clock on "stuck since".
        stall.current = { time: v.currentTime, since: at };
        wedgeRemounts = 0;
        setMediaState((cur) => {
          // Say so ONCE, on the edge. A wall that recovered is as much of a
          // finding as one that froze, and without this line a log capture
          // cannot tell twenty-three minutes of healthy playback from
          // twenty-three minutes of a frozen picture - they look identical,
          // because the stall is CMS state and never reached the buffer.
          if (cur !== null) console.warn("[signage-loop] recovered, playing");
          return cur === null ? cur : null;
        });
        return;
      }
      const stuckMs = at - stall.current.since;

      // NO at-end intervention, NO wedge detector, NO remount. Reverted on
      // 2026-08-13 after four attempts made the loop worse, not better.
      //
      // The history, because it is the reason this block is now empty. This
      // panel will not restart a <video> in place: seeking to 0 completes and
      // wedges the decoder. Each fix removed one seek and revealed another -
      // the loop point, the hidden-layer rewind, the ended handler - and the
      // watchdog built to rescue the wedges started mistaking healthy spin-ups
      // for wedges and remounting the player about once per lap. `recovered`
      // reached 84. A five-second hitch is a poor loop; a remount every lap is
      // a worse one, and neither was measured before being shipped.
      //
      // So the element is left alone. `loop` is the platform's own mechanism
      // and `onEnded` is the documented fallback; both stay. What is gone is
      // every attempt to out-guess them from a 500ms interval.
      //
      // The stall REPORTING below stays. Knowing a wall is frozen, and being
      // able to say why, is worth having even when nothing acts on it - and it
      // is the measurement that has to come before any further attempt.
      const duration = v.duration;
      void duration;

      // A confirmed decoder wedge (see WEDGE_REMOUNT_MS): data is buffered
      // ahead of a playhead that is not moving, the element is not paused and
      // never got past "metadata". Nothing else has worked for it in 30s -
      // `play()` is already being re-asked on every tick - so rebuild the
      // element. A cold start succeeds most of the time on this panel; the cap
      // keeps a bad run from turning into a remount per lap.
      if (
        stuckMs >= WEDGE_REMOUNT_MS &&
        wedgeRemounts < WEDGE_REMOUNT_MAX &&
        v.readyState <= 1 &&
        v.buffered.length > 0 &&
        v.buffered.end(v.buffered.length - 1) - v.currentTime > 2
      ) {
        wedgeRemounts += 1;
        console.warn(
          `[signage-loop] WEDGED ${Math.round(stuckMs / 1000)}s - remounting (#${wedgeRemounts}/${WEDGE_REMOUNT_MAX}) ${layerState(v)}`
        );
        stall.current = { time: v.currentTime, since: at };
        setNudge((n) => n + 1);
        return;
      }

      // Ask the element WHY, while it is still stuck. `readyState` and
      // `networkState` distinguish the three stalls that look identical on a
      // wall: starved of bytes, decoded-and-ended, or dead with an error.
      // Captured before the remount, which destroys the evidence.
      if (stuckMs >= STALL_PLAY_MS) {
        // Also on the edge only - this runs every 500ms while stuck, and a line
        // per tick would push the rest of the buffer out inside a minute.
        setMediaState((cur) => {
          // The player's reason first when there is one: "the manifest never
          // loaded" explains the element's state, where the element's state on
          // its own points at the wrong bug entirely.
          const reason = manifestError.current;
          const next = reason
            ? `${describeMedia(v, stuckMs)} · ${reason}`
            : describeMedia(v, stuckMs);
          if (cur === null) console.warn(`[signage-loop] STALLED · ${next}`);
          return next;
        });
      }
      if (stuckMs >= STALL_PLAY_MS) {
        // try/catch AND .catch: `play()` rejects on some engines and throws
        // outright on others (jsdom does). An exception here escapes into the
        // interval, kills the tick, and takes the playhead with it - the
        // watchdog would become the thing that freezes the wall.
        try {
          const p: unknown = v.play();
          if (p && typeof (p as Promise<void>).catch === "function") {
            void (p as Promise<void>).catch(() => {});
          }
        } catch {
          /* nothing else to try until the remount below */
        }
      }
    }, WATCHDOG_MS);
    return () => window.clearInterval(id);
  }, []);

  const head = timeline ? playheadAt(timeline, clock()) : null;

  // Are we currently holding a locally-advanced position in THIS playlist?
  // False whenever `heldIndex` is unset, or names a DIFFERENT playlist than
  // the wall clock currently does - a real schedule change always wins over
  // stale local state, exactly like `lastOnBreak`/`onBreakEdge` above.
  const holdingHere =
    heldIndex.current !== null &&
    !!head?.playlist &&
    heldIndex.current.playlistId === head.playlist.id;
  // The index content-layer item selection actually uses: the held position
  // while holding, otherwise the wall clock's own answer - unchanged from
  // before this existed.
  const effectiveIndex = holdingHere
    ? heldIndex.current!.index
    : (head?.index ?? 0);

  /*
   * The dead set, filtered down to what is STILL excluded right now.
   *
   * Recomputed every render (cheap - a handful of ids, never the whole
   * playlist) rather than cached, because the whole point is that this set
   * can SHRINK between renders as entries age past DEAD_RETRY_MS - a cached
   * "how many are dead" would miss exactly the recovery this exists to allow.
   */
  const nowMs = clock().getTime();
  const currentlyDead = new Set<string>();
  for (const [id, deadAtMs] of dead.current) {
    if (nowMs - deadAtMs < DEAD_RETRY_MS) currentlyDead.add(id);
  }
  const deadCount = currentlyDead.size;

  /*
   * A BREAK OWNS THE WALL WHILE IT RUNS.
   *
   * `timeline.breaks` was resolved, shipped and ignored by this surface -
   * campaign breaks have never played on a Foyer panel, and the ad layer being
   * built on top needs this to exist first.
   *
   * Clock-derived like everything else here: `breakAt` is a pure function of
   * the instant, so nothing is banked and nothing rewinds. When the break ends
   * `playheadAt` answers wherever the wall clock now points, which IS resuming
   * the loop mid-item - the reference implementation needs `calculatePlayback
   * Times` and a 1s rewind constant only because it walks an index.
   *
   * `breakAt` also carries the linear-channel refusal, so a channel block is
   * never interrupted no matter what the marker grid says.
   */
  /*
   * Vistar ads (see `vistarAds.ts`). A filled ad slot is merged in as one more
   * campaign break, so the break path below plays it - no second player.
   */
  const adBreaks = useVistarAds({
    screenId,
    token,
    apiBase,
    timeline,
    clock,
    tick,
    currentItemId: lastItemId.current,
    deadIds: currentlyDead,
  });
  const playTimeline = useMemo(
    () => (timeline ? withAdBreaks(timeline, adBreaks) : null),
    [timeline, adBreaks]
  );
  const brk = playTimeline ? breakAt(playTimeline, clock()) : null;
  /*
   * A break whose every item is dead gives the wall BACK to the loop.
   *
   * `pickPlayable` returning null is the "all of it failed" answer, and for a
   * playlist that correctly means black - there is nothing else to show. For a
   * break it means the opposite: the loop underneath is perfectly good content
   * and is what the screen would have been showing anyway. Falling through to
   * it is the whole of "an unplayable ad is skipped, never a black screen".
   */
  const breakItem = brk
    ? pickPlayable(brk.brk.items, brk.index, currentlyDead)
    : null;
  const onBreak = breakItem !== null;
  // A background<->break EDGE, either direction - see the layer-pair
  // computation below for why this matters and `lastOnBreak`'s own comment
  // for why `null` (not `false`) means "no prior commit, never an edge".
  const onBreakEdge =
    lastOnBreak.current !== null && lastOnBreak.current !== onBreak;
  pendingOnBreak.current = onBreak;

  const item =
    breakItem ??
    (head?.item
      ? pickPlayable(head.playlist.items, effectiveIndex, currentlyDead)
      : null);

  // An ORDINARY item-to-item edge, mirroring `onBreakEdge` exactly - see
  // `lastItemId`'s doc comment above for why this exists. `breakItem` is
  // deliberately included (an item change INTO or OUT OF a break is still a
  // real edge for decoder-pressure purposes), so this is computed on `item`
  // itself, not on `head`'s own index.
  const onItemEdge =
    lastItemId.current !== null &&
    item !== null &&
    lastItemId.current !== item.id;
  pendingItemId.current = item?.id ?? null;

  /*
   * Which loop video (if any) to keep warm under an image break. Chosen ONCE,
   * at the first render of the break, from what the wall clock was showing -
   * the clock keeps moving during the break and must not swap the held element
   * for a different one. A catalogue title (`contentAssetId`) is held too -
   * that is what "Pump Me Up" is - and for that the licence resolution below
   * follows the HELD item, so its URL stays put through the break.
   */
  if (!onBreak) {
    pausedLoop.current = null;
  } else if (pausedLoop.current === null && item?.kind === "image") {
    const loopNow = head?.item
      ? pickPlayable(head.playlist.items, effectiveIndex, currentlyDead)
      : null;
    if (loopNow && loopNow.kind === "video") {
      pausedLoop.current = loopNow;
    }
  }
  const heldLoop =
    onBreak && item?.kind === "image" ? pausedLoop.current : null;

  /*
   * Engage or release the hold, based on what `item` actually turned out to
   * be this render. Staged (not written directly) for the same reason
   * `pendingOnBreak` is: this runs during render, and render can execute
   * more than once (StrictMode) without a commit ever happening - only the
   * effect above, which runs strictly after a real commit, may be trusted.
   */
  if (!onBreak && head?.playlist && item) {
    const shouldHold = item.kind === "video" && item.durationSeconds === 0;
    if (shouldHold && !holdingHere) {
      // Landing on an unmeasured video for the first time - fresh entry
      // into this playlist, or just advanced off a real-duration item.
      // Seeded from wherever the wall clock currently points, which is
      // exactly "resume mid-item" for the FIRST time we see it.
      pendingHeldIndex.current = {
        playlistId: head.playlist.id,
        index: effectiveIndex,
      };
    } else if (!shouldHold && holdingHere) {
      // The (locally-advanced) position we reached is no longer an
      // unmeasured video - hand authority back to the wall clock, which is
      // correct-by-design for a real duration or a non-video item.
      pendingHeldIndex.current = null;
    }
  }

  /**
   * The video's own natural end, for an item this surface is HOLDING - see
   * `heldIndex`'s doc comment. Walks forward exactly like `pickPlayable`
   * (skips dead items, wraps), but needs the INDEX it landed on, not just
   * the item, to keep `heldIndex` in sync for the next natural end.
   *
   * Deliberately mutates `heldIndex.current` directly rather than staging
   * it: this runs from a real DOM `ended` event, strictly after a commit,
   * never during render - the same reasoning that already lets `markDead`
   * and `adoptActiveVideo` below mutate their refs directly.
   */
  const advancePastNaturalEnd = (endedItemId: string) => {
    // Reads `heldIndex.current` FRESH rather than the closure's
    // `holdingHere` - this fires from a real DOM event, and the commit
    // effect that applies a staged hold only mutates the ref, it does not
    // itself force a re-render (nothing needs it to: the 1Hz tick already
    // picks up a fresh ref on its own next pass). An `ended` event firing
    // inside that window would otherwise read a STALE `holdingHere` still
    // captured from the render that did the staging, before the ref
    // actually committed, and wrongly no-op a genuine natural end.
    const held = heldIndex.current;
    if (
      onBreak ||
      !held ||
      !head?.playlist ||
      held.playlistId !== head.playlist.id
    ) {
      return;
    }
    const items = head.playlist.items;
    const currentIndex = held.index;
    // Stale event from an element whose turn has already passed (a
    // pre-rolling layer's `ended`, or one that fired after something else
    // already moved the hold) - ignore rather than double-advance.
    if (items[currentIndex]?.id !== endedItemId) return;
    const nextIndex = pickPlayableIndex(items, currentIndex + 1, currentlyDead);
    if (nextIndex === null) return; // everything dead - hold in place
    heldIndex.current = { playlistId: head.playlist.id, index: nextIndex };
    forceAfterNaturalEnd((v) => v + 1);
  };

  const markDead = (id: string) => {
    // DIAGNOSTIC ONLY: an item exiled for DEAD_RETRY_MS is otherwise silent.
    console.warn(`[signage-loop] item marked DEAD · id=${id}`);
    dead.current.set(id, nowMs);
    setDeadVersion((v) => v + 1);
  };

  /* -- on-panel media cache (TIZEN-9) ------------------------------------- */

  // The timeline is the source of truth for "what this wall is meant to play",
  // so it drives BOTH halves of the cache: what to fetch, and what to drop.
  // Keying the effect on the timeline's own revision means a config refresh
  // that changes nothing does not re-queue the whole loop.
  // `playTimeline`, not `timeline`: a filled ad slot's creative is cached
  // like any break item, in the minute between the ask and the marker. It
  // changes only when the schedule or the filled slots do.
  useEffect(() => {
    const timeline = playTimeline;
    if (!timeline) return;
    initSignageCache();
    // Every non-manifest asset the timeline can reach. A manifest is a playlist
    // of segments rather than a file: caching the .mpd while its segments stay
    // remote buys nothing.
    const urls = Array.from(
      new Set(
        timeline.playlists
          .flatMap((p) => p.items)
          // Break items are ours to cache too. They were missed while the
          // panel ignored `timeline.breaks` entirely, and a break plays on the
          // marker whether or not a hotel's uplink is having a good minute -
          // which is exactly when it must already be on disk.
          .concat(timeline.breaks.flatMap((b) => b.items))
          .map((i) => i.assetUrl)
          // Pictures a SLIDE draws are ours too, and a wall on a hotel's
          // uplink benefits from them being local exactly as much as a
          // full-screen image does. Taken from the resolved templates rather
          // than the items, because a slide's media lives in its values.
          .concat(
            (timeline.templates ?? []).flatMap((t) =>
              Object.values(t.values ?? {}).filter((v) =>
                v.startsWith("/foyer-assets/")
              )
            )
          )
          // A `url` item carries no `assetUrl` at all and is dropped here by
          // the emptiness check, which is the intended outcome: a third
          // party's web page is not ours to copy onto a panel's disk, and a
          // cached one would be a stale page nobody could tell was stale.
          .filter((u) => !!u && !/\.(mpd|m3u8)(\?|$)/i.test(u))
      )
    );
    // Drop what is no longer scheduled BEFORE queueing what is - otherwise a
    // full cache refuses the new loop while still holding the old one. The
    // empty-set guard lives inside evictStale, where it cannot be forgotten.
    evictStale(urls);
    queueSignageAssets(urls);
  }, [playTimeline]);

  /* -- licences for catalogue titles (PLAYER-6) --------------------------- */

  /**
   * A protected title's URL and licence, resolved when its turn comes.
   *
   * Not in the timeline, and it cannot be: a licence token lives five minutes
   * while a timeline is cached for six hours, so an embedded licence would be
   * stale before the loop's second lap - and would put a live credential into a
   * document the CMS renders in a browser.
   *
   * ## The preview asks too, and the reasoning that said otherwise was wrong
   *
   * This used to read "only a PANEL asks - the CMS preview has no screen token
   * and no business minting a licence". That sounded like a property of DRM and
   * was a property of our routing: `resolvePlayback` takes an ASSET and returns
   * a manifest and a token, the licence is minted by the server, and the CDM
   * that consumes it is the browser's - which a desktop browser has. Ruled by
   * Sigge: *"hardware asks backend which creates key and the asset is playable
   * - this has nothing to do with the hardware. Previews must be able to handle
   * DRM protected content."*
   *
   * What the old sentence cost: a protected channel previewed as a stalled
   * video with `ready nothing · net empty · buffered none`, which is the
   * documented signature of an unrelated bug. An operator scheduling the news
   * had no way to see their own wall.
   *
   * So the preview asks its own premise-scoped route, authenticated as the
   * operator. The panel keeps asking with its screen token. One resolver, one
   * shape, two credentials.
   */
  const [licensed, setLicensed] = useState<ResolvedItemPlayback | null>(null);
  /** The play route's own refusal code, when it gave one. */
  const [licenceRefusal, setLicenceRefusal] = useState<string | null>(null);
  // The licence belongs to the item whose video is (or is being kept) in a
  // layer: during an image break that is the HELD loop video, not the image.
  const licenceItem = heldLoop ?? item;
  const contentAssetId =
    licenceItem && needsLicence(licenceItem)
      ? licenceItem.contentAssetId
      : undefined;
  useEffect(() => {
    if (!contentAssetId) {
      setLicensed(null);
      setLicenceRefusal(null);
      return;
    }
    const caller: PlaybackCaller | null = token
      ? { kind: "screen", screenId, token }
      : premiseId
        ? { kind: "operator", premiseId }
        : null;
    if (!caller) {
      setLicensed(null);
      setLicenceRefusal(null);
      return;
    }
    let alive = true;
    // Out, on the console, both ends: the failure side of this call already
    // logs (`[signage-licence] refused ...`), but a SUCCESS that resolves
    // with no usable licence URL - a real, observed shape, not a hypothetical
    // - looked identical to "never resolved at all" from the outside. This
    // line is what tells the two apart without guessing from Shaka's own
    // (much later, much vaguer) 6001.
    console.warn(
      `[signage-licence] resolving ${contentAssetId} as ${caller.kind}`
    );
    void resolveItemPlayback({ apiBase, caller, contentAssetId }).then((r) => {
      if (!alive) return;
      console.warn(
        `[signage-licence] resolved ${contentAssetId}: url=${r.playback ? "yes" : "no"} licenceUrl=${r.playback?.licenceUrl ? "yes" : "no"} playready=${r.playback?.playreadyLicenceUrl ? "yes" : "no"} refusal=${r.refusal ?? "none"}`
      );
      setLicensed(r.playback);
      // A refusal is a DECISION and gets said out loud. Without this the
      // rights rule below renders black and explains nothing, which is the
      // third variant of the same swallowing this file has been through.
      setLicenceRefusal(r.refusal);
    });
    return () => {
      alive = false;
    };
  }, [contentAssetId, token, premiseId, screenId, apiBase]);

  /*
   * A new item starts with no verdict against it.
   *
   * `useManifestPlayback` clears on the way IN, but only when it is enabled -
   * so a manifest that failed followed by a plain JPEG would leave the failure
   * on screen, attributed to a picture that loaded perfectly. Child effects run
   * before parent effects, and the failure itself arrives later still (it is an
   * async rejection), so clearing here cannot erase a live report.
   */
  const activeItemId = item?.id;
  // Every item change and every break edge restarts the settle window.
  const prerollSettleAt = useRef(Date.now() + PREROLL_SETTLE_MS);
  useEffect(() => {
    prerollSettleAt.current = Date.now() + PREROLL_SETTLE_MS;
  }, [activeItemId, onBreak]);
  useEffect(() => {
    manifestError.current = null;
    setManifestReason(null);
    // A stale key system would attribute the PREVIOUS item's CDM to this one,
    // and a loop mixing a protected channel with clear pictures would report a
    // key system for the pictures.
    setKeySystem(null);
  }, [activeItemId]);

  /**
   * When the current item began, and how many times the wall has actually
   * moved on since boot - see `itemStartedAt`/`transitionsSinceBoot`'s own
   * doc comments (`@shared/types/foyer`) for why a single heartbeat needed
   * these. Keyed on the same `activeItemId` as the effect above, and
   * declared before the report effect below, so a fresh item always has an
   * up-to-date ref by the time the report reads them in the same commit.
   *
   * The first item after a mount/boot is not a "transition" - there is
   * nothing before it to have moved on FROM - so the counter starts absent
   * and only increments from the SECOND distinct item onward.
   */
  const itemStartedAtRef = useRef<number | null>(null);
  const transitionsRef = useRef(0);
  useEffect(() => {
    if (activeItemId === undefined) return;
    if (itemStartedAtRef.current !== null) transitionsRef.current += 1;
    itemStartedAtRef.current = clock().getTime();
    // Proof-of-play: this item is now on screen. One report per item start,
    // keyed on the material (see signageProofOfPlay.ts). A campaign break item
    // and a background-loop item are reported apart: only the first is
    // "signage" in the reports; the loop is the customer's own material.
    if (item) {
      const play = signagePlayEvent(item, head?.playlist.name, { onBreak });
      if (play) trackContentPlay(play);
    }
    // `clock()` reads `now`/`at`/the real clock+skew - it is a function
    // reference, not reactive state, so it is deliberately not a dep here;
    // depending on it would re-run this every render instead of only on a
    // genuine item change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeItemId]);

  // Cache the asset that just finished. The background queue usually gets
  // there first; this is what fills the cache on a panel where the queue is
  // starved, and it is the autoplayer's original path.
  const playedRef = useRef<string | null>(null);
  useEffect(() => {
    const url = item?.assetUrl;
    if (!url) return;
    const previous = playedRef.current;
    playedRef.current = url;
    if (previous && previous !== url) cacheAfterPlay(previous);
  }, [item?.assetUrl]);

  /* -- report ------------------------------------------------------------ */

  // Tell the heartbeat what is actually on this wall. Only the panel reports:
  // the CMS preview renders this same component in a browser that has no
  // heartbeat and no business speaking for a screen.
  //
  // `blocked` is deliberately narrower than "not playing": a screen with
  // nothing scheduled is idle, which is correct and common, and calling that a
  // fault would light up every out-of-hours lobby in the estate. Blocked means
  // something SHOULD be on and nothing can be.
  const isPanel = source.kind === "screen";
  const pollLabel =
    status.state === "http"
      ? `http ${status.code}`
      : status.state === "ok"
        ? "ok"
        : status.state;
  const state: "playing" | "idle" | "blocked" = item
    ? "playing"
    : head?.item ||
        status.state === "http" ||
        (status.state === "offline" && !timeline)
      ? "blocked"
      : "idle";
  useEffect(() => {
    if (!isPanel) return;
    reportSignagePlayback({
      state,
      revision: timeline?.revision,
      // The same sentence the panel puts on screen when it has nothing to
      // show. "Idle" alone cannot separate "outside its hours" from "nothing
      // was ever scheduled here" from "the scheduled playlist is empty", and
      // those need completely different actions from completely different
      // people.
      idleReason:
        state === "playing"
          ? undefined
          : explainEmpty({ status, timeline, head, deadCount, now: clock() })
              .headline,
      playlistName: head?.playlist.name,
      itemIndex: item ? effectiveIndex + 1 : undefined,
      itemCount: head?.playlist.items.length,
      // Absent while idle/blocked - a stale timestamp from whatever was
      // playing before would misreport how long "nothing" has been on.
      itemStartedAt:
        item && itemStartedAtRef.current !== null
          ? new Date(itemStartedAtRef.current).toISOString()
          : undefined,
      transitionsSinceBoot: transitionsRef.current || undefined,
      failedItems: deadCount || undefined,
      recoveries: nudge || undefined,
      poll: pollLabel,
      media: mediaState ?? undefined,
      keySystem: keySystem ?? undefined,
      // Reported even when it is zero-ish. "The panel's clock is fine" is a
      // finding too - it is what rules drift out as the explanation next time a
      // screen goes quietly idle.
      clockSkewSeconds: skewSeconds,
      // See the field's own doc comment (THE BREAK FREEZE) - this is the one
      // question a future occurrence could not previously answer remotely:
      // did THIS client ever believe it was in a break at all.
      breakActive: onBreak,
    });
    // `head` and `item` are rebuilt by the resolver on EVERY 1Hz tick, so
    // depending on the objects would re-report once a second forever. The
    // fields listed below are exactly what a report contains: when one of them
    // changes the report is stale, and when none do it is not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPanel,
    state,
    timeline?.revision,
    head?.playlist.name,
    head?.playlist.items.length,
    item?.id,
    effectiveIndex,
    deadCount,
    nudge,
    pollLabel,
    mediaState,
    keySystem,
    // In the list because it is IN the payload. Left out, a panel whose clock
    // drifted after boot would keep reporting the skew it had at its first
    // poll - which is precisely the number somebody would be reading to decide
    // whether drift explains an idle screen.
    skewSeconds,
    onBreak,
  ]);

  const overlay = diagnostics ? (
    <Diagnostics
      screenId={screenId}
      timeline={timeline}
      status={status}
      head={head}
      effectiveIndex={effectiveIndex}
      playing={item}
      deadCount={deadCount}
      recovered={nudge}
      mediaState={mediaState}
      manifestReason={manifestReason}
      licenceRefusal={licenceRefusal}
      clockSkewSeconds={skewSeconds}
      onBreak={onBreak}
      now={clock()}
      scrubbed={Boolean(at)}
    />
  ) : null;

  const resolvedLogoPlacement: ChannelLogoPlacement = logoPlacement ?? {
    x: DEFAULT_CHANNEL_LOGO_OFFSET_X,
    y: DEFAULT_CHANNEL_LOGO_OFFSET_Y,
    width: DEFAULT_CHANNEL_LOGO_WIDTH,
  };
  // Only a curated EFFECT channel gets a logo, and only once it has one set -
  // an ordinary customer loop never carries `logoUrl` (nothing in the CMS
  // offers the control outside `asChannel`), and this is the defensive
  // backstop for that invariant rather than the only thing enforcing it.
  // Invisible for the whole of a campaign break: the mark belongs to the
  // channel's content, not to the break that is playing over it.
  const channelLogo =
    !onBreak && head?.playlist.effect && head.playlist.logoUrl ? (
      <ChannelLogoOverlay
        logoUrl={head.playlist.logoUrl}
        placement={resolvedLogoPlacement}
      />
    ) : null;
  // CONTENT-26: whoever supplied the item playing RIGHT NOW, bottom-right -
  // opposite the channel's own mark, because on an effect channel carrying a
  // catalogue title both are true at once. Effect channels only (ruled
  // 2026-09-27), and only when the asset actually carries a `logo` image:
  // `supplierLogoUrl` is absent for a live channel by construction, so this
  // never double-draws the channel mark. The corner comes from the authority,
  // not from here.
  const supplierLogo =
    !onBreak && head?.playlist.effect && item?.supplierLogoUrl ? (
      <ChannelLogoOverlay
        claimant="supplier-logo"
        testId="supplier-logo-overlay"
        logoUrl={item.supplierLogoUrl}
        placement={resolvedLogoPlacement}
      />
    ) : null;

  /* -- render ----------------------------------------------------------- */

  // Nothing scheduled is a legitimate answer, not a fault. Black is the right
  // thing for a screen with nothing to say - louder than a message nobody
  // asked for, in a room where the screen is furniture.
  if (!item) return <Black>{overlay}</Black>;

  const muted = item.muted || !timeline?.soundEnabled;

  // THE LAYER PAIR. What is on screen now, plus what is on screen next.
  //
  // A single element meant every item change tore one down and built another,
  // and a fresh <video> on these panels is a fresh decoder: several hundred
  // milliseconds to seconds of BLACK between clips, on a wall, every time.
  // (The black poster made it black rather than grey; it did not make it
  // shorter.) Mounting the next item alongside the current one lets it fetch
  // and decode its first frame while the current clip is still playing, so the
  // change is an opacity flip between two ready pictures.
  //
  // Two layers, never more: a hardware decoder is a pooled resource here and
  // pre-rolling the whole playlist would exhaust it - which is the very
  // failure that made a screen die after three clips.
  // During a break the next thing is the next item IN the break, not the next
  // slide of the loop - pre-rolling the loop's item would decode something that
  // is not on next.
  const upcoming =
    onBreak && brk
      ? nextPlayable(brk.brk.items, brk.index, currentlyDead)
      : nextPlayable(head!.playlist.items, effectiveIndex, currentlyDead);
  /*
   * At a background↔break EDGE (`onBreakEdge`) OR an ORDINARY item-to-item
   * edge (`onItemEdge`), skip pre-rolling `upcoming` for exactly this one
   * render.
   *
   * A break edge swaps BOTH layers at once - nothing carries a key across,
   * so the commit tears down up to two elements and mounts up to two more in
   * the same pass, and "torn down" is a React-tree fact, not a hardware one:
   * a pooled decoder is not guaranteed released the instant its element
   * leaves the DOM (see the cleanup effect below). Doubling the simultaneous
   * fresh-acquisition count at exactly the moment two are also being
   * released is the shape of the "a screen dies after three clips" failure
   * this comment already names.
   *
   * 2026-09-16: `onItemEdge` extends the SAME mitigation to ORDINARY
   * rotation, because "only ONE fresh mount ever happens per step" turned
   * out not to hold for manifest (DASH/Shaka) content specifically -
   * `useManifestPlayback` is enabled for BOTH layers whenever the item is a
   * video manifest, independent of `active` (that is the whole point of
   * pre-rolling one), so the PRE-ROLLING layer already holds a live
   * Shaka/MediaSource instance before its turn ever comes. An ordinary step
   * therefore still overlaps one fresh Shaka creation (the NEW `upcoming`)
   * with one `void player?.destroy()` (the OUTGOING layer, unawaited) in the
   * same commit - smaller than a break edge's up-to-two-and-two, but the
   * identical shape of overlap, and the one this surface had never actually
   * exercised with real long clips before CONTENT-29's natural-end-advance
   * fix let ordinary rotation complete for the first time. Reproduced twice
   * on hasselbacken/301 (Philips): a real item finishing, its `ended` event
   * correctly firing, the next item correctly becoming active per every
   * telemetry field this surface reports - and the wall going black anyway,
   * indefinitely, with nothing to signal it.
   *
   * ⚠️ REASONED, NOT HARDWARE-VERIFIED, same standing as the break-edge
   * mitigation below it was modelled on - see [[signage-playback-surface]].
   * Safe-by-construction regardless: it can only REDUCE peak simultaneous
   * decoder/Shaka-instance pressure, costs at most one tick (~1s) of
   * deferred pre-roll on whichever item plays after the one already
   * showing, and changes nothing about the very first clip after a boot
   * (`lastItemId.current === null` there, so `onItemEdge` is false, exactly
   * mirroring `lastOnBreak`).
   */
  /*
   * A catalogue title is not warmed behind the one on screen.
   *
   * 2026-10-15, QB24C: every time the next film's layer was mounted the video on
   * screen was found PAUSED ~1.5-2 s later (`nudge play() ... paused=true`), a
   * 2 s black in the middle of a clip - at startup, after breaks and at clip
   * changes alike. Starting a second Shaka/MediaSource instance next to a
   * playing one stops the playing one on this panel. And the warm-up bought
   * nothing for a title that had to be licensed first: the licence step rebuilt
   * the layer anyway.
   */
  const upcomingWarmable = !upcoming?.contentAssetId;
  // Two VIDEOS never start up together: see PREROLL_SETTLE_MS.
  const prerollSettled =
    item.kind !== "video" ||
    upcoming?.kind !== "video" ||
    Date.now() >= prerollSettleAt.current;
  const pair =
    upcoming &&
    upcoming.id !== item.id &&
    !onBreakEdge &&
    !onItemEdge &&
    upcomingWarmable &&
    prerollSettled
      ? [item, upcoming]
      : [item];
  // The held loop video rides along, UNDER the break (same key as when it was
  // the active layer, so React keeps the element).
  const layers =
    heldLoop && !pair.some((l) => l.id === heldLoop.id)
      ? [...pair, heldLoop]
      : pair;

  /*
   * THE NATIVE PATH. One clip, one surface, no pair.
   *
   * Everything the layer pair below does depends on holding two decoders at
   * once, and AVPlay has exactly one. The gap it exists to hide is covered
   * differently here: the next source is opened and prepared in the moment
   * after the current one completes (`preloadNext`), which is the only moment
   * a single surface is free.
   *
   * Images keep the HTML path either way - an `<img>` needs no decoder anyone
   * is competing for, and routing them through a video player would be
   * theatre.
   */
  /*
   * A catalogue title waits for its licence.
   *
   * Black, briefly, rather than a flash of the wrong thing - and BLACK RATHER
   * THAN THE CLEAR RENDITION if the licence is refused. That is the rule
   * `storefront-playback` exists to enforce and it has to survive the trip to
   * the panel: on a rights-holder's material, quietly playing the unprotected
   * copy is a contract breach rather than a graceful degradation.
   *
   * The diagnostics overlay still renders, so the preview says why.
   */
  /*
   * ⚠️ `token` is deliberately NO LONGER part of this condition.
   *
   * It used to read `item.contentAssetId && token && !licensed`, so the rights
   * rule this guard exists to enforce - black rather than the clear rendition
   * when a licence is refused - applied on panels and silently did not apply in
   * the preview, which fell through and played whatever URL the timeline
   * carried. Now that the preview resolves a licence of its own, it is held to
   * the same rule, which is the only version of it that means anything.
   */
  if (needsLicence(item) && !licensed) {
    return <Black>{overlay}</Black>;
  }

  /*
   * A TEMPLATE SLIDE.
   *
   * Handled here rather than inside `MediaLayer` because it is not media: it
   * has no decoder, no cache entry, no failure to skip on, and none of the
   * layer-pair machinery below applies to it. The pair exists so a clip can be
   * decoded before its turn; a slide is DOM and is ready the moment it mounts.
   *
   * The design and its values ride the timeline (`timeline.templates`), so this
   * makes no request and cannot fail slowly - which is why the whole thing is
   * looked up by id and drawn, or not drawn.
   */
  if (item.kind === "template") {
    const slide = (timeline?.templates ?? []).find(
      (t) => t.instanceId === item.templateInstanceId
    );
    // The resolver already warned (`template_unavailable`) and the preview
    // renders that warning; the wall stays silent. Black is the documented
    // answer for "nothing to show".
    if (!slide) return <Black>{overlay}</Black>;
    return (
      <div
        style={{ position: "fixed", inset: 0, background: "#000" }}
        data-testid="signage-surface"
        data-revision={timeline?.revision}
      >
        <TemplateSlide
          slide={slide}
          timezone={timeline?.timezone ?? "UTC"}
          // The surface already re-renders at 1Hz, so a clock region ticks
          // without a timer of its own - and it reads the SERVER-corrected
          // clock, not the panel's, which is routinely wrong.
          now={clock}
          // Web blocks keep their page loaded across loop cycles - one
          // Elfsight view per screen, not one per cycle.
          keepAliveWeb
        />
        {overlay}
        {channelLogo}
        {supplierLogo}
      </div>
    );
  }

  /*
   * ⛔ A MANIFEST NEVER GOES TO AVPLAY, even on a panel that has it.
   *
   * Measured on a real Samsung, 2026-08-30: a linear channel reported
   * `prepare failed: PLAYER_ERROR_NOT_SUPPORTED_FORMAT · mobileindex.mpd ·
   * dash · widevine session open`. The Widevine session had opened - so this
   * was not a missing CDM and not a licence fault - and AVPlay still refused
   * the package. Alcom's output is `avc3` (inband parameter sets) with
   * trick-mode AdaptationSets and `stpp` subtitles: ordinary DVB-DASH that a
   * browser plays and a native TV demuxer need not.
   *
   * `nenda-autoplayer` has never hit this, because on Tizen its player matrix
   * sends a LINEAR CHANNEL to Shaka and only files to AVPlay
   * (`VideoPlayer.tsx`: `platform === Tizen ? type === LinearChannel ?
   * <ShakaPlayer> : <AvPlayPlayer>`). That split is described at the top of
   * `avplay.ts` and Foyer implemented only half of it - every `.mpd` went to
   * the native player.
   *
   * The distinction is the URL, not the item kind: a channel item and a VOD
   * title both arrive as `kind: "video"` and both resolve to a manifest. What
   * AVPlay is good at is the thing it was ported for - a cached MP4 on a panel
   * whose `<video>` element will not decode one.
   */
  const manifestItem = isAbrItem(item, licensed?.url ?? item.assetUrl);

  if (nativePlayer && item.kind === "video" && !manifestItem) {
    const cached = playbackCacheHit(item);
    // A solo playlist never advances: `playheadAt` is a modulo, so it keeps
    // returning this same item and nothing else will ever restart it.
    const solo = !upcoming || upcoming.id === item.id;
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#000",
          overflow: "hidden",
        }}
        data-testid="signage-surface"
        data-revision={timeline?.revision}
      >
        <AvPlaySurface />
        <AvPlayLayer
          key={item.id}
          // The resolved URL wins: for a protected title it is the rendition
          // the licence was minted FOR, and the timeline's copy may be the
          // clear one.
          src={licensed?.url ?? playbackSrc(item)}
          nextSrc={solo ? undefined : playbackSrc(upcoming!)}
          muted={muted}
          loop={solo}
          // Always false here now: a manifest never reaches this branch. Kept
          // wired rather than deleted because AVPlay's DASH path is still the
          // right one for a packaged FILE, and the option is one condition
          // above, not a capability that was removed.
          isDash={false}
          onFailed={() => {
            // A cached copy that will not open is a cache problem, not a
            // content one: drop the entry and let the next resolve go to the
            // network. Marking the item dead here would let a stale cache
            // empty a playlist one file at a time.
            if (cached) clearCacheEntry(item.assetUrl);
            else markDead(item.id);
          }}
          onMedia={setMediaState}
        />
        {overlay}
        {channelLogo}
        {supplierLogo}
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        overflow: "hidden",
      }}
      data-testid="signage-surface"
      data-revision={timeline?.revision}
    >
      {layers.map((layer) => (
        <MediaLayer
          // `nudge` only participates for the ACTIVE item, so the watchdog can
          // rebuild a wedged player without also tearing down the innocent
          // layer that is quietly pre-rolling behind it.
          /*
           * The SAME key whether a layer is active or not - this is what makes
           * the hand-over free. Keying the active layer differently (say, with
           * the watchdog's `nudge` appended only when active) means the
           * pre-rolled element gets a NEW key the moment its turn comes, React
           * throws it away and builds a fresh one, and the black gap that the
           * layer pair exists to remove comes straight back.
           *
           * `nudge` therefore applies to both. A stall rebuilds the pre-roll
           * as well, which costs one re-warm on an event that is already a
           * fault - much cheaper than being wrong on every clip change.
           */
          // The slot is part of the key ONLY for a solo loop, where both
          // halves carry the same item id and would otherwise collide into one
          // element - which is precisely the single element that cannot restart
          // itself on this hardware.
          key={`${layer.id}:${nudge}`}
          item={layer}
          active={layer.id === item.id}
          held={!!heldLoop && layer.id === heldLoop.id && layer.id !== item.id}
          retryOnce={onBreak}
          muted={muted}
          // Only the ACTIVE layer has a resolution: the licence was minted for
          // the item whose turn it is. A pre-rolling layer keeps the timeline's
          // URL and, if it is protected, will get its own licence when it
          // becomes active - which is the correct moment, since a token minted
          // a clip early could expire before its turn.
          // `licensed` was minted for the loop's video, which during an image
          // break is the HELD layer and not the image on screen. Handing it to
          // the image (old rule: "the active layer") painted the film's .m3u8
          // into an <img> for the first render of every break - the "image
          // marked DEAD 52 ms in" failure.
          playback={
            layer.id === (heldLoop?.id ?? item.id) && needsLicence(layer)
              ? licensed
              : null
          }
          onFailed={() => markDead(layer.id)}
          onActiveVideo={adoptActiveVideo}
          onNaturalEnd={() => advancePastNaturalEnd(layer.id)}
          // Only the ACTIVE layer's failure is worth reporting: a pre-rolling
          // layer that cannot load will report for itself when its turn comes,
          // and reporting both would attribute one layer's fault to whatever is
          // on screen.
          onManifestError={
            layer.id === item.id ? noteManifestError : noteNothing
          }
          onKeySystem={layer.id === item.id ? setKeySystem : noteNoKeySystem}
        />
      ))}
      {overlay}
      {channelLogo}
      {supplierLogo}
    </div>
  );
}

/**
 * One picture in the layer pair: the item on screen, or the one warming up.
 *
 * An inactive layer is fully rendered and fully loaded - it just has no
 * opacity. That is the entire trick: by the time the playhead reaches it, the
 * decoder has already done its work.
 */
function MediaLayer({
  item,
  active,
  held,
  retryOnce,
  muted,
  playback,
  onFailed,
  onActiveVideo,
  onManifestError,
  onKeySystem,
  onNaturalEnd,
}: {
  item: SignageItem;
  active: boolean;
  /**
   * Hidden on purpose and meant to come back: pause in place, do NOT `load()`.
   * `load()` resets the pipeline and with it the playhead, which is exactly
   * what holding the element through an image break exists to avoid.
   */
  held: boolean;
  /** Give a failed image one more try before reporting it dead. */
  retryOnce: boolean;
  muted: boolean;
  /** Resolved URL + licence for a catalogue title, or null. */
  playback: ResolvedItemPlayback | null;
  onFailed: () => void;
  /** Hands the ACTIVE video element to the stall watchdog. */
  onActiveVideo: (el: HTMLVideoElement | null) => void;
  /** Why a manifest never loaded, so the wall can say more than "stalled". */
  onManifestError: (reason: string | null) => void;
  /** Which key system the CDM chose, so the fleet can read it remotely. */
  onKeySystem: (keySystem: string | null) => void;
  /**
   * The ACTIVE video reached a genuine end (only meaningful for an item
   * with no declared duration - see `loop` below). Called on every `ended`
   * regardless; the caller (`advancePastNaturalEnd`) is the one that knows
   * whether it is currently holding this exact item and no-ops otherwise,
   * so this layer does not need to know its own duration semantics.
   */
  onNaturalEnd: () => void;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);

  // Play when shown, rewind when hidden. A layer that keeps running unseen
  // would be a second clip's worth of decode for nothing, and would arrive at
  // its turn already half over.
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    // DIAGNOSTIC ONLY: timers that report what the element did AFTER it was
    // handed the screen, cleared if the layer changes first.
    let cleanup: (() => void) | undefined;
    if (active) {
      onActiveVideo(v);
      try {
        // What the element looked like at the instant it was handed the screen.
        // If a hand-over is failing, this is the line that says whether the
        // twin was ready or still cold.
        console.warn(
          `[signage-loop] show · item=${item.id} readyState=${v.readyState} net=${v.networkState} t=${v.currentTime.toFixed(2)} dur=${Number.isFinite(v.duration) ? v.duration.toFixed(1) : "?"}`
        );
        const p: unknown = v.play();
        if (p && typeof (p as Promise<void>).then === "function") {
          // Was play() accepted? A cold element (readyState 0, no source yet)
          // can reject it, and the watchdog treats the resulting paused
          // element as idle rather than stalled - so nothing else would say.
          void (p as Promise<void>).then(
            () =>
              console.warn(`[signage-loop] play() resolved · item=${item.id}`),
            (e: unknown) =>
              console.warn(
                `[signage-loop] play() REJECTED · item=${item.id} · ${e instanceof Error ? `${e.name}: ${e.message}` : String(e)}`
              )
          );
        }
        const probes = [3_000, 20_000].map((ms) =>
          window.setTimeout(() => {
            console.warn(
              `[signage-loop] +${ms / 1000}s · item=${item.id} ${layerState(v)}`
            );
          }, ms)
        );
        // 2026-10-12, QB24C: a cold layer's play() is aborted by the manifest
        // hook's own load() ("interrupted by a new load request"), nothing
        // asks again, and the watchdog ignores a paused element by design - so
        // the picture froze for the rest of a 10-minute clip. While THIS layer
        // is the one on screen, a paused, connected, unfinished, error-free
        // video is never intended: ask again. It can only ever act on the
        // active layer (the effect is torn down the moment it is hidden), so
        // it cannot wake a hidden twin.
        let nudges = 0;
        const nudge = window.setInterval(() => {
          try {
            if (!v.isConnected || !v.paused || v.ended || v.error) return;
            if (!v.currentSrc) return;
            nudges += 1;
            if (nudges <= 3) {
              console.warn(
                `[signage-loop] nudge play() #${nudges} · item=${item.id} ${layerState(v)}`
              );
            }
            void v.play().catch(() => undefined);
          } catch {
            /* the watchdog owns recovery */
          }
        }, 4_000);
        cleanup = () => {
          probes.forEach((t) => window.clearTimeout(t));
          window.clearInterval(nudge);
        };
      } catch {
        /* the watchdog owns recovery */
      }
    } else if (held) {
      try {
        console.warn(
          `[signage-loop] hold · item=${item.id} (pause in place) ${layerState(v)}`
        );
        v.pause();
      } catch {
        /* harmless */
      }
    } else {
      try {
        console.warn(
          `[signage-loop] hide · item=${item.id} (pause + load) ${layerState(v)}`
        );
        v.pause();
        // `load()`, NOT `currentTime = 0`, and this is the whole hand-over.
        //
        // Rewinding with a seek is the operation a Samsung QB24C will not
        // recover from: the seek completes, the decoder wedges, and the element
        // is silently poisoned. Invisible here - it is off screen - right up
        // until its turn comes and it refuses to play, which is what kept
        // `recovered` climbing even after the layer pair was given a twin to
        // hand over to. The twin was arriving already broken.
        //
        // `load()` resets the pipeline instead, and the cost that would
        // normally argue against it does not apply: this element has a whole
        // clip's duration to re-prime out of sight, and `preload="auto"` plus
        // the on-panel cache mean it is re-reading from local disk.
        v.load();
      } catch {
        /* an element mid-load can refuse this; harmless */
      }
    }
    return cleanup;
  }, [active, held, onActiveVideo, item.id]);

  // Hand the decoder back on the way out. An Android WebView does not release
  // one just because React removed the node, and they are scarce: leak a few
  // and the next clip gets none at all.
  useEffect(() => {
    const v = ref.current;
    return () => {
      if (!v) return;
      try {
        v.pause();
        // Nothing was ever decoded, so there is no decoder to hand back -
        // and calling load() on an element that never loaded is a no-op that
        // some engines (jsdom among them) complain about.
        if (v.readyState === 0 && !v.currentSrc) return;
        v.removeAttribute("src");
        v.load();
      } catch {
        /* best effort - cleanup must never throw */
      }
    };
  }, []);

  // A catalogue title resolves to a manifest at timeline-build time (DASH by
  // preference, HLS for old material); a media file resolves to itself.
  // Everything below the `src` is identical.
  // The resolved URL wins where there is one: for a protected title it is the
  // rendition the licence was minted FOR, and the timeline may carry the clear
  // one alongside it.
  const sourceUrl = playback?.url ?? playbackSrc(item);
  const isManifest = isAbrItem(item, sourceUrl);
  /**
   * Turn Shaka's failure into a sentence somebody can act on.
   *
   * The commonest cause by far is the one the CMS preview cannot avoid: a
   * PROTECTED title needs a Widevine licence, a licence needs a screen token,
   * and the preview has neither and never will (tokens are minted at pairing,
   * for a device). Without this hint the operator sees a stalled video and no
   * reason, on a channel that plays perfectly on a real panel.
   *
   * The hint is added on FAILURE only, never up front: a clear channel
   * previews fine with no licence at all, and refusing every catalogue title
   * in the preview would break the case that works.
   */
  const reportManifestError = useCallback(
    (failure: ShakaFailure | null) => {
      if (failure === null) {
        onManifestError(null);
        return;
      }
      /*
       * ⚠️ This used to append "protected channels need a paired screen" to a
       * 6xxx. That sentence is GONE, and its removal is the point of the
       * change that brought it here: the preview mints its own licence now, so
       * a DRM failure in the preview is a real DRM failure and not a statement
       * about where you are looking. Telling somebody to go find a television
       * would send them away from a fault that is in front of them.
       *
       * What replaces it is the licence route's own refusal, rendered
       * separately - it is the server's decision and it arrives with a code.
       */
      const code = failure.code === null ? "" : ` (shaka ${failure.code})`;
      onManifestError(`${failure.text}${code}`);
    },
    // `item.id` rather than the object: a new item is a new report, but the
    // same item re-rendering must not rebuild the player.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.id]
  );

  useManifestPlayback(
    ref,
    sourceUrl,
    item.kind === "video" && isManifest,
    playback?.licenceUrl ?? null,
    playback?.playreadyLicenceUrl ?? null,
    reportManifestError,
    onKeySystem
  );

  const cached = playback ? null : playbackCacheHit(item);
  const playSrc = playback?.url ?? playbackSrc(item);

  /*
   * Put `src` BACK when something has torn it off the element.
   *
   * The decoder-release cleanup above ends in `removeAttribute("src")`, and
   * `<React.StrictMode>` (`src/tv/main.tsx`) runs that cleanup once immediately
   * after the first mount, before remounting. Its guard - "nothing was ever
   * decoded, so there is no decoder to hand back" - asks
   * `readyState === 0 && !currentSrc`. But `currentSrc` is set by the browser's
   * RESOURCE SELECTION task, not by assigning the attribute, so whether that
   * guard holds is a RACE between React's passive-effect flush and that task:
   *
   *   cleanup first  -> currentSrc still "" -> guard returns -> the wall plays
   *   selection first -> currentSrc set     -> src stripped  -> the wall is dead
   *
   * Nothing re-sources it, because the `src` PROP never changed and React has
   * no attribute to reconcile. The signature is the one this file's own
   * watchdog prints - `ready nothing · net empty · buffered none`, and
   * `error === null`, because nothing failed: it was torn down, not refused.
   * A warm cache answering instantly is enough to flip the race, which is why
   * it survived weeks of demos and then died on a reload with nothing changed.
   *
   * Re-asserting is the fix rather than a stricter guard: an effect has to
   * survive being mounted twice WHATEVER the timing, and dropping the
   * `!currentSrc` clause would still lose the race once `readyState` reached 1.
   * The equality check makes this a no-op on every render that did not lose it.
   *
   * A manifest is Shaka's element to own (see `useManifestPlayback`), so it is
   * left alone here for the same reason it carries no `src` attribute at all.
   */
  useEffect(() => {
    const v = ref.current;
    if (!v || item.kind !== "video" || isManifest || !playSrc) return;
    if (v.getAttribute("src") === playSrc) return;
    v.setAttribute("src", playSrc);
    v.load();
  }, [playSrc, isManifest, item.kind]);

  // A cached file that will not open falls back to the network and clears the
  // entry, WITHOUT reporting a playback failure. Several dead entries in a row
  // would otherwise trip the circuit breaker and blank the wall - turning a
  // cache miss into an outage, which is worse than the problem the cache
  // solves.
  // Which <img> element this is; bumped by the retry timer ONLY, so the rebuild
  // lands after the beat and not at the error itself.
  const [imgKey, setImgKey] = useState(0);
  const imgRetried = useRef(false);
  const retryTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (retryTimer.current !== null) window.clearTimeout(retryTimer.current);
    },
    []
  );
  const onMediaError = () => {
    // DIAGNOSTIC ONLY (2026-10-12): a campaign image was marked DEAD 52ms after
    // its break started, and nothing said why. Path only - never the query,
    // which can carry a signed token.
    try {
      console.warn(
        `[signage-loop] media error · item=${item.id} kind=${item.kind} cached=${cached ? "yes" : "no"} src=${playSrc.split("?")[0]}`
      );
    } catch {
      /* a diagnostic must never be the reason a failure is lost */
    }
    if (cached) {
      clearCacheEntry(item.assetUrl);
      return;
    }
    // A break image is dead for DEAD_RETRY_MS (5 min) on its first error, and
    // breaks come every ~3 min: one transient failure silently cost the
    // advertiser this break AND the next. Rebuild the <img> once, after a
    // beat, before giving up.
    if (retryOnce && item.kind === "image" && !imgRetried.current) {
      imgRetried.current = true;
      retryTimer.current = window.setTimeout(() => {
        retryTimer.current = null;
        setImgKey((k) => k + 1);
      }, 1_500);
      return;
    }
    // The retry is still waiting out its beat: the verdict is the NEW element's.
    if (retryTimer.current !== null) return;
    onFailed();
  };

  const common = {
    position: "absolute" as const,
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: (item.fit === "contain" ? "contain" : "cover") as
      | "contain"
      | "cover",
    // No transition: a cross-fade between two pieces of unrelated content
    // looks like a fault, and on a slow panel it costs a compositor pass per
    // frame. The swap should be invisible, not decorative.
    opacity: active ? 1 : 0,
    // Opacity alone is not hiding on a Samsung: a hidden <video> can keep
    // painting its frame through the hardware video plane. 2026-10-15, QB24C:
    // after every break the same frozen frame - the NEXT film's first frame,
    // from the twin mounted behind the resumed video - showed for seconds.
    // `visibility: hidden` takes the element out of painting altogether, and
    // the explicit stacking puts the one on screen above anything waiting.
    visibility: (active ? "visible" : "hidden") as "visible" | "hidden",
    // 0 / -1, NEVER above 0: the channel and supplier logos and the diagnostics
    // overlay are positioned siblings drawn after the layers with no z-index of
    // their own, so a layer at z-index 1 paints OVER them (the channel logo
    // vanished after the visibility change). At 0 they win by document order.
    zIndex: active ? 0 : -1,
    background: "#000",
  };

  if (item.kind === "video") {
    return (
      <video
        ref={ref}
        // A manifest is handed to Shaka instead - see useManifestPlayback.
        {...(isManifest ? {} : { src: playSrc })}
        // Autoplay only for the layer that is actually on screen; the other
        // one is primed by `preload` and started when its turn comes.
        autoPlay={active}
        muted={muted}
        playsInline
        /*
         * Loop UNLESS this item has no declared duration.
         *
         * The original bug this guarded against: `playheadAt` is a modulo
         * over the loop, so a playlist holding one clip returns the same
         * item id every pass, React keeps the very same element, which had
         * already reached its end - nothing restarted it, so the wall sat
         * on the final frame and a reboot bought exactly one more play.
         * Looping unconditionally fixed that, but it has a cost this file
         * paid for months without knowing it (CONTENT-29): `loop` SUPPRESSES
         * the browser's own `ended` event entirely, per spec, whether or not
         * anything is listening for it. For a `durationSeconds: 0` item -
         * "play to its natural end", the estimate this surface substitutes
         * for it is explicitly NOT meant to drive playback (see
         * `itemSeconds`'s doc comment) - `ended` is the ONLY real signal
         * this surface has for when such a clip is actually done, so it
         * cannot also be the item looping itself forever.
         *
         * `onEnded` below now restarts the SAME clip explicitly whenever
         * advancing does not actually move anywhere (a genuinely solo
         * survivor) - the exact safety net `loop` used to provide, just
         * driven by the event this surface needs anyway rather than by the
         * browser's own restart.
         */
        loop={item.durationSeconds !== 0}
        // Kills the WebView's grey play-glyph placeholder - see BLACK_POSTER.
        poster={BLACK_POSTER}
        // The whole point of the inactive layer: fetch and decode NOW, so the
        // hand-over is a flip rather than a load.
        preload="auto"
        onError={onMediaError}
        // For a looping (real-duration) item, a WebView that ignores `loop`
        // fires `ended` instead - and an ended element nothing restarts is
        // the frozen wall again, so this stays the fallback for that case
        // regardless. For a NON-looping (unmeasured) item, this is now the
        // PRIMARY signal: `onNaturalEnd()` tells the surface to advance,
        // and if advancing lands back on this same item (a solo survivor),
        // the reload-and-play below is what actually restarts it, since
        // nothing else will.
        //
        // `load()` rather than a seek, for the third time in this file and
        // the same reason each time: seeking to 0 completes on a QB24C and
        // leaves the decoder wedged. This was the last place still doing it.
        onEnded={(e) => {
          if (active) onNaturalEnd();
          const v = e.currentTarget;
          try {
            v.load();
            const p: unknown = v.play();
            if (p && typeof (p as Promise<void>).catch === "function") {
              void (p as Promise<void>).catch(() => {});
            }
          } catch {
            /* the watchdog will rebuild it */
          }
        }}
        style={common}
      />
    );
  }
  if (item.kind === "url") {
    /*
     * A web page, full-bleed.
     *
     * `objectFit` is meaningless for a document, so `common` is spread and the
     * fit dropped: a page lays itself out to the viewport it is given, and the
     * only honest framing is the whole screen.
     *
     * ## The sandbox, and the mistake it is a correction of
     *
     * This shipped as `sandbox="allow-scripts"` with no `allow-same-origin`,
     * reasoned as "anything pointed at a wall is untrusted". That is true and
     * it was still the wrong call, because omitting `allow-same-origin` gives
     * the document an **opaque origin** - and an opaque origin breaks the
     * ordinary things a real page is built out of:
     *
     *  - `localStorage` / `sessionStorage` THROW on access rather than fail;
     *  - cookies are gone, so anything with a session is signed out;
     *  - and the one that actually bit: a fetch to its OWN relative API path
     *    becomes a CROSS-ORIGIN request. The browser then demands CORS headers
     *    from a server that never needed them, blocks the response, and the
     *    page renders its own "cannot load" message. Measured on the first real
     *    customer page tried (an Actic class schedule, whose `/api/classes`
     *    returns no `Access-Control-Allow-Origin` because nothing had ever
     *    asked it from another origin). It worked in a browser tab and failed
     *    only in Foyer, which is exactly the shape this comment exists to stop
     *    somebody re-deriving.
     *
     * `allow-scripts` + `allow-same-origin` is the famously dangerous pair, but
     * ONLY when the framed document is same-origin with its embedder: it can
     * then reach `parent.document`, strip the `sandbox` attribute off its own
     * element and reload out of the box. Cross-origin - which is every real
     * signage page - `allow-same-origin` restores nothing but the page's OWN
     * origin, and normal cross-origin isolation still keeps it out of this
     * document. So the pair is granted, and the same-origin case is refused
     * below instead of being paid for by every legitimate page.
     *
     * **`allow-top-navigation` stays out, and that is the token that matters
     * here.** On a wall with no keyboard and nobody watching, a page that can
     * navigate the panel away from Foyer is the whole threat; a page that can
     * read its own cookies is not.
     *
     * ⚠️ **A page that refuses to be framed renders as a WHITE BOX, silently.**
     * `X-Frame-Options` / `frame-ancestors` denials produce no `onError`, no
     * console entry the panel reports, and no failed-item count - the wall just
     * turns white, which is worse than the black we treat as correct. There is
     * nothing this renderer can do about it: the refusal happens inside the
     * browser's own load. It is caught one layer earlier instead, by the CMS
     * probe at authoring time, which is the only place a human is looking.
     */
    const { objectFit: _objectFit, ...page } = common;
    return (
      <iframe
        title=""
        src={playSrc}
        sandbox={iframeSandbox(playSrc)}
        // A wall is not interactive, and a page that could take focus could
        // also take the D-pad away from the surface behind it.
        tabIndex={-1}
        scrolling="no"
        style={{ ...page, border: 0, pointerEvents: "none" }}
      />
    );
  }
  if (item.kind !== "image") {
    /*
     * An unknown kind renders as BLACK, not as a broken image.
     *
     * This used to be an unconditional `<img>` fallback, which meant any kind
     * this build did not know about - an older panel meeting a newer timeline,
     * which is the normal state of a fleet - painted a browser's broken-image
     * glyph across a hotel lobby. Black is the surface's documented answer for
     * "nothing to show": the wall stays silent and the preview explains.
     */
    const { objectFit: _fit, ...blank } = common;
    return <div style={blank} />;
  }
  // `key` rebuilds the element for the retry: an <img> that already errored
  // does not re-request on its own.
  return (
    <img
      key={imgKey}
      src={playSrc}
      alt=""
      onError={onMediaError}
      style={common}
    />
  );
}

/**
 * Attach a player when — and only when — the item is a manifest
 * (`CONTENT-13`).
 *
 * ## Why this is narrow on purpose
 *
 * A signage wall runs a bare `<video src>` deliberately: it has to recover from
 * a power cut with no JavaScript state to rebuild, and every decoder it holds is
 * one the next clip cannot have. A catalogue title arrives as a manifest (the
 * catalogue's protocol vocabulary is `hls | dash | other` and there is no
 * progressive option), and a MediaSuite WebView plays neither natively — so a
 * player is unavoidable for that ONE case and must not touch any other.
 *
 * A media file keeps the plain `src` path it always had, byte for byte.
 *
 * ## Why Shaka rather than hls.js
 *
 * **DASH is the preferred protocol** (`signagePlayableResource`), so the wall
 * has to be able to play it — and Shaka plays DASH *and* HLS, which makes the
 * player a superset of the preference rather than a constraint on it. The first
 * version of this hook loaded `hls.js` and the model was bent to fit it: HLS
 * got "preferred" because that was what the bundle could open. A player that
 * cannot honour the platform's choice turns that choice into a refusal.
 *
 * `hls.js` stays in the CMS, where a preview only ever opens one rendition.
 *
 * ## What this does NOT make safe
 *
 * ⚠️ It says nothing about ENCODING. `panel-video-encoding` measured
 * plays-five-seconds-then-stalls, dead after ~3 loops, on a profile the
 * catalogue does not state — and a manifest whose renditions are High@4.2 will
 * fail exactly that way. `CONTENT-13` is not finished until a real panel has
 * looped a real title for longer than a demo, and Shaka on a MediaSuite is now
 * part of what that bench is checking.
 */
/**
 * What Shaka's rejection actually means, in words somebody can act on.
 *
 * The first version of this printed `shaka ${code}` and then Shaka's own
 * `message`, which for a `shaka.util.Error` is the string "Shaka Error 6001" -
 * so the wall said **"shaka 6001 Shaka Error 6001"** and explained nothing
 * twice. The number is worth keeping exactly once, because it is what a support
 * conversation looks up; everything else has to be a sentence.
 *
 * The CATEGORY is the load-bearing part. Shaka encodes it as the thousands
 * digit, and it decides whether the "you need a paired screen" hint is true or
 * a misdirection: a 6xxx is about licensing, but a 1xxx is a URL that did not
 * answer and a 4xxx is a manifest that would not parse. Blaming pairing for
 * either of those sends somebody to fix something that is already fine - which
 * is the whole failure mode this file has just been through from the other
 * direction.
 */
export interface ShakaFailure {
  /** Shaka's numeric code, for a support lookup. Null if it gave none. */
  code: number | null;
  /** The thousands digit: 1 network, 3 media, 4 manifest, 5 streaming, 6 DRM. */
  category: number | null;
  /** Plain English, no code in it. */
  text: string;
}

export function describeShakaFailure(err: unknown): ShakaFailure {
  const e = (err ?? {}) as { code?: unknown; message?: unknown };
  const code = typeof e.code === "number" ? e.code : null;
  const category = code === null ? null : Math.floor(code / 1000);

  const text = ((): string => {
    switch (code) {
      // The one the CMS preview produces, every time, on a protected channel:
      // the manifest declares a key system and no licence server is configured,
      // so Shaka finds no usable configuration at all.
      case 6001:
        return "the stream is encrypted and no licence server is configured for it";
      case 6012:
        return "the stream is encrypted and no licence server was given";
      case 6010:
        return "the stream is encrypted but names no DRM system";
      case 6007:
        return "the licence server refused the request";
      case 6008:
        return "the licence server's response was rejected";
      case 1001:
      case 1002:
        return "the stream URL did not answer";
      case 1003:
        return "the stream URL timed out";
      default:
        break;
    }
    switch (category) {
      case 1:
        return "the stream could not be fetched";
      case 3:
        return "this screen could not decode the stream";
      case 4:
        return "the manifest could not be read";
      case 5:
        return "the stream stopped part-way through";
      case 6:
        return "the stream is protected and the licence step failed";
      default:
        return typeof e.message === "string" && e.message
          ? e.message.slice(0, 90)
          : "the player refused it without saying why";
    }
  })();

  return { code, category, text };
}

/**
 * H.264 High profile, by its codec string - `avc1.64..` / `avc3.64..`
 * (`profile_idc` 0x64 = 100 decimal = High; Main is `0x4D`/77, Baseline
 * `0x42`/66 - see ISO/IEC 14496-10).
 *
 * 2026-09-21 (hasselbacken/302): the SAME hardware-confirmed finding
 * `panel-video-encoding` already holds for video Foyer encodes itself -
 * *"Profile: Main, not High - High@4.2 was refused/software-decoded"* -
 * turned out to also apply to Alcom catalogue manifests, which Foyer does
 * not control the encode of. Both a broken and a working effect-channel
 * item shared the identical ladder shape: every rung Main profile except
 * the top 1080p one, which was High. The broken item's log showed a
 * permanent `readyState=0 net=0 t=0.00` - a hard decode refusal from frame
 * zero, the exact signature this file's own hardware measurements already
 * name for High profile on this SoC.
 */
export function isHighProfileH264(codecs: string): boolean {
  return /\bavc[13]\.64/i.test(codecs);
}

/**
 * Remove `<Representation>` blocks a caller disallows from a DASH manifest's
 * raw XML, before Shaka ever parses it - the response-filter technique this
 * file already uses for licence-URL rewriting (see `useManifestPlayback`'s
 * DRM config), applied to the manifest fetch instead of the licence one.
 *
 * Exists because Shaka's own `restrictions` (`abrCapConfig`,
 * `streamPlayback.ts`) has no codec/profile field at all - confirmed against
 * the bundled player's own `meetsRestrictions`, which checks only
 * width/height/pixels/frameRate/channelsCount/bandwidth. A rendition can be
 * comfortably under the bitrate and pixel cap and still be a profile this
 * exact hardware cannot decode; capping bitrate/pixels alone does not
 * exclude it.
 *
 * Fails OPEN on every uncertain case, on purpose - a manifest surgery bug is
 * strictly worse than doing nothing:
 * - a block that cannot be parsed (no `codecs` attribute at all, e.g. a
 *   thumbnail sprite track or an audio rendition) is never touched, matching
 *   `panel-video-encoding`'s own documented thumbnail-Representation trap;
 * - if removing the disallowed blocks would leave ZERO video representations
 *   in the whole document, nothing is removed - Shaka is left to raise its
 *   own `RESTRICTIONS_CANNOT_BE_MET`/decode-refusal error against the
 *   original manifest rather than being handed a document with no video
 *   AdaptationSet content at all, which is a different and worse failure
 *   shape than either.
 *
 * When an `<AdaptationSet>` carries exactly one `<Representation>` (this
 * catalogue's actual shape: one main rendition per set, each paired with a
 * sibling trick-mode AdaptationSet via the DASH-IF adaptation-set-switching
 * SupplementalProperty - never several bitrates stacked in one set),
 * removing that one disallowed Representation leaves an EMPTY
 * `<AdaptationSet>` shell behind. That is invalid DASH - Shaka's own parser
 * rejects it outright with `DASH_EMPTY_ADAPTATION_SET` (error 4003, "the
 * manifest could not be read"), a hard parse failure on the WHOLE manifest,
 * not a soft decode-refusal on just the disallowed rendition. This is the
 * actual root cause of the 2026-09-21 incident: the strip fix shipped and
 * ran on both platforms, produced a structurally invalid manifest for
 * every item whose only 1080p/lowest rung was High profile, and Shaka
 * refused the whole document - so every AdaptationSet left with zero
 * Representations after stripping is removed in its entirety instead of
 * being left as an empty shell.
 */
export function stripDisallowedVideoRepresentations(
  mpdXml: string,
  isDisallowed: (codecs: string) => boolean
): string {
  const repRe =
    /<Representation\b[^>]*?(?:\/>|>[\s\S]*?<\/Representation\s*>)/gi;
  const adaptRe =
    /<AdaptationSet\b[^>]*?(?:\/>|>[\s\S]*?<\/AdaptationSet\s*>)/gi;
  const codecsRe = /\bcodecs="([^"]*)"/i;
  const isVideoCodecs = (codecs: string) => /^(avc|hev|hvc)/i.test(codecs);

  type Block = { start: number; end: number; codecs: string | null };
  const repBlocks: Block[] = [];
  let match: RegExpExecArray | null;
  while ((match = repRe.exec(mpdXml))) {
    const codecsMatch = match[0].match(codecsRe);
    repBlocks.push({
      start: match.index,
      end: match.index + match[0].length,
      codecs: codecsMatch ? codecsMatch[1] : null,
    });
  }
  if (repBlocks.length === 0) return mpdXml;

  const videoReps = repBlocks.filter(
    (b) => b.codecs && isVideoCodecs(b.codecs)
  );
  const disallowedReps = videoReps.filter((b) => isDisallowed(b.codecs!));
  if (disallowedReps.length === 0) return mpdXml;
  // Refuse to strip down to no video at all - see the doc comment above.
  if (disallowedReps.length >= videoReps.length) return mpdXml;

  const disallowedSet = new Set(disallowedReps);

  type Range = { start: number; end: number };
  const adaptBlocks: Range[] = [];
  let adaptMatch: RegExpExecArray | null;
  while ((adaptMatch = adaptRe.exec(mpdXml))) {
    adaptBlocks.push({
      start: adaptMatch.index,
      end: adaptMatch.index + adaptMatch[0].length,
    });
  }

  // Decide, per AdaptationSet, whether to drop just its disallowed
  // Representations or the whole set (when that would empty it out).
  const removeRanges: Range[] = [];
  for (const set of adaptBlocks) {
    const repsInSet = repBlocks.filter(
      (b) => b.start >= set.start && b.end <= set.end
    );
    if (repsInSet.length === 0) continue;
    const disallowedInSet = repsInSet.filter((b) => disallowedSet.has(b));
    if (disallowedInSet.length === 0) continue;
    if (disallowedInSet.length === repsInSet.length) {
      removeRanges.push({ start: set.start, end: set.end });
    } else {
      for (const b of disallowedInSet) removeRanges.push(b);
    }
  }

  let out = mpdXml;
  // Remove from the end so earlier offsets stay valid.
  for (const r of [...removeRanges].sort((a, c) => c.start - a.start)) {
    out = out.slice(0, r.start) + out.slice(r.end);
  }
  return out;
}

// Exported for direct `renderHook` coverage of two 2026-09-17/18 incident
// investigations: a StrictMode-double-invoke race that turned out NOT to
// exist (kept as general robustness coverage), and the real confirmed gap -
// no timeout anywhere in this chain (see STARTUP_WATCHDOG_MS below). Same
// pattern this file already uses for its other pure/testable internals
// (`describeShakaFailure`, `isIdleElement`, ...). Going through the full
// `SignageSurface` render tree cannot pin the timing precisely enough: too
// much sits between a poll resolving and this hook's own effect actually
// firing to control it from outside.
export function useManifestPlayback(
  ref: React.RefObject<HTMLVideoElement | null>,
  url: string,
  enabled: boolean,
  /** Widevine licence URL, token included, or null for clear content. */
  licenceUrl: string | null,
  /** PlayReady licence URL for the same stream, or null. */
  playreadyLicenceUrl: string | null,
  /** Told why the manifest never loaded. `null` clears a previous failure. */
  onError: (failure: ShakaFailure | null) => void,
  /** Told which key system the device's CDM actually chose, once, on success. */
  onKeySystem: (keySystem: string | null) => void
): void {
  // Startup retries spent on THIS url. Keyed by url so a new item starts at 0.
  const [startup, setStartup] = useState<{ url: string; n: number }>({
    url,
    n: 0,
  });
  const attempt = startup.url === url ? startup.n : 0;
  useEffect(() => {
    const video = ref.current;
    if (!video || !enabled) return;
    onError(null);

    let disposed = false;
    let player: { destroy(): Promise<void> } | null = null;
    // Set only if the native fallback (below) was actually armed for this
    // attempt, so cleanup removes exactly the listeners it added - and
    // nothing else, on the far more common path where Shaka just works.
    let removeNativeListeners: (() => void) | null = null;
    // Set once the manifest response filter (High-profile strip, below) is
    // actually registered, so cleanup can unregister exactly that instance.
    let removeManifestFilter: (() => void) | null = null;

    /*
     * THE STARTUP WATCHDOG (2026-09-17/18 incident, hasselbacken/302 Philips).
     *
     * Nothing below this line used to time out. A hung `attach()`,
     * `configure()`, or `load()` - a network stall, a DNS issue, a CDN
     * request that never settles - left the promise chain simply never
     * progressing: no error, no recovery, the element sat at `readyState=0`
     * for hours while the heartbeat kept reporting "playing" (that field
     * reflects the RESOLVER's estimate of what should be on screen, not
     * whether a frame ever actually decoded). `nenda-autoplayer`'s own
     * `ShakaPlayer.tsx` has exactly this watchdog already
     * (`SHAKA_STARTUP_FAIL_MS`); this is the same fix, sized the same.
     *
     * Cleared the moment `load()` actually resolves. If it fires first,
     * `watchdogFired` gates every later checkpoint below so a slow success
     * or the eventual real rejection never ALSO reports past the failure
     * this already sent - the wall gets one verdict, not two.
     */
    let watchdogFired = false;
    const watchdog = setTimeout(() => {
      watchdogFired = true;
      if (disposed) return;
      onError({
        code: null,
        category: null,
        text: `manifest never started within ${STARTUP_WATCHDOG_MS / 1000}s`,
      });
      if (attempt < STARTUP_RETRY_MAX) {
        console.warn(
          `[signage] manifest never started - rebuilding the player (retry ${attempt + 1}/${STARTUP_RETRY_MAX})`
        );
        // Re-runs this effect: the cleanup below destroys the hung player and
        // the next pass builds a fresh one.
        setStartup({ url, n: attempt + 1 });
      }
    }, STARTUP_WATCHDOG_MS);

    void import("shaka-player/dist/shaka-player.compiled")
      .then(async (shaka) => {
        if (disposed || watchdogFired) return;
        const lib = (shaka as { default?: unknown }).default ?? shaka;
        type NetworkingEngine = {
          registerResponseFilter(
            filter: (
              type: number,
              response: { data: ArrayBuffer; uri?: string }
            ) => void
          ): void;
          unregisterResponseFilter(
            filter: (
              type: number,
              response: { data: ArrayBuffer; uri?: string }
            ) => void
          ): void;
        };
        const api = lib as {
          polyfill: { installAll(): void };
          net?: { NetworkingEngine?: { RequestType?: { MANIFEST?: number } } };
          Player: new (media?: HTMLMediaElement) => {
            load(uri: string): Promise<void>;
            destroy(): Promise<void>;
            configure(config: unknown): void;
            getNetworkingEngine?(): NetworkingEngine | null;
          };
        };
        // Idempotent, and required on an Android WebView: without it Shaka
        // refuses EME/media-source shapes the panel reports non-standardly.
        api.polyfill.installAll();
        const instance = new api.Player();
        player = instance;
        await (
          instance as unknown as { attach(v: HTMLMediaElement): Promise<void> }
        ).attach(video);
        if (disposed || watchdogFired) return;

        /*
         * Strip High-profile video renditions out of the manifest BEFORE
         * Shaka ever sees them - see `stripDisallowedVideoRepresentations`'s
         * own doc comment for why this exists (Shaka's `restrictions` has no
         * codec field at all) and why it is a response filter on the
         * MANIFEST fetch rather than an ABR restriction.
         *
         * Registered before `load()` so it is in place for the very first
         * manifest fetch, unregistered on cleanup below - same lifecycle as
         * the licence-URL request filter this pattern was modelled on.
         */
        const netEngine = instance.getNetworkingEngine?.() ?? null;
        const manifestType =
          api.net?.NetworkingEngine?.RequestType?.MANIFEST ?? 0;
        const stripHighProfile = (
          type: number,
          response: { data: ArrayBuffer; uri?: string }
        ) => {
          if (type !== manifestType) return;
          try {
            const text = new TextDecoder().decode(response.data);
            // Only a DASH manifest looks like this - never touch an HLS
            // playlist or anything else routed through the same filter.
            if (!/<MPD[\s>]/i.test(text)) return;
            const stripped = stripDisallowedVideoRepresentations(
              text,
              isHighProfileH264
            );
            if (stripped !== text) {
              response.data = new TextEncoder().encode(stripped).buffer;
            }
          } catch {
            // Fails open: an unreadable response is left exactly as Shaka
            // would have received it without this filter at all.
          }
        };
        netEngine?.registerResponseFilter(stripHighProfile);
        if (netEngine) {
          removeManifestFilter = () =>
            netEngine.unregisterResponseFilter(stripHighProfile);
        }
        /*
         * EVERY key system we can license, and the DEVICE chooses.
         *
         * This call did not exist until PLAYER-6, which meant the wall could
         * not play protected content on ANY platform - Shaka with no configured
         * server has nowhere to send a challenge, so a DRM stream failed in a
         * way that read like a network fault. The URLs already carry their
         * signed token.
         *
         * ⚠️ It offered Widevine ALONE until 2026-08-30, on the strength of a
         * comment claiming the reference player did the same. It does not:
         * `nenda-autoplayer` configures both and says why - *"Samsung panels
         * differ in which CDM they actually ship."* Which CDM is present is a
         * fact about the panel, unknowable from here, and Alcom packages both
         * into one manifest. Shaka ignores a server for a key system the
         * device does not have, so offering both costs nothing and removes a
         * whole class of unexplained black screen.
         */
        const servers: Record<string, string> = {};
        if (licenceUrl) servers["com.widevine.alpha"] = licenceUrl;
        if (playreadyLicenceUrl) {
          servers["com.microsoft.playready"] = playreadyLicenceUrl;
        }
        // Out, on the console: whatever `[signage-licence] resolved ...`
        // said a moment ago, THIS is the value this specific effect
        // invocation's closure actually has when it builds Shaka's config -
        // the only way to tell a stale-closure race (an earlier invocation,
        // still in flight from before the licence resolved, reaching this
        // point after a newer one already has the real URLs) from every
        // other candidate explanation already ruled out.
        console.warn(
          `[signage] configuring drm: keys=${Object.keys(servers).join(",") || "(none)"}`
        );
        if (Object.keys(servers).length > 0)
          instance.configure({ drm: { servers } });
        await instance.load(url);
        // A genuine success cancels the watchdog outright, whether or not it
        // already fired - a late-but-real load must not be second-guessed by
        // a timer that was only ever a stand-in for "nothing happened yet".
        clearTimeout(watchdog);
        if (disposed) return;
        // The watchdog already reported a timeout failure before this late
        // success arrived - clear it, or the wall's own diagnostics would
        // keep showing "manifest never started" for a stream that is, in
        // fact, now playing.
        if (watchdogFired) onError(null);
        /*
         * Which CDM actually won, once - because nothing else can say.
         *
         * Offering two key systems means a success no longer implies WHICH one
         * played, and that is the single fact that decides whether a fleet of
         * Samsung panels needs PlayReady at all. It rides the console log
         * rather than the heartbeat: the heartbeat's `media` field is for
         * faults, and a working stream must not fill it.
         *
         * ⚠️ Never the licence URL - the token is an ES256 JWT in a query
         * string. Only the key system name.
         */
        const chosen = (
          instance as unknown as { drmInfo?(): { keySystem?: string } | null }
        ).drmInfo?.()?.keySystem;
        if (chosen) {
          console.warn(`[signage] key system: ${chosen}`);
          /*
           * ⚠️ And OUT, on the heartbeat - not only to the console.
           *
           * This line shipped to console alone on 2026-08-30 and was
           * immediately unreadable: the panels this question is about are
           * bolted to walls in hotels with no devtools on them. It is the same
           * defect this file has now grown four times - the only component
           * that knows keeps it to itself - committed in the very change that
           * documented it.
           */
          onKeySystem(chosen);
        }
      })
      .catch((err: unknown) => {
        /*
         * ⚠️ **This used to be an empty catch**, commented "the surface's own
         * stall watchdog owns recovery". That watchdog's recovery was REMOVED
         * on 2026-08-13 - `nudge` is pinned at zero and the surface now
         * reports a stall and acts on nothing - so the comment described a
         * mechanism that no longer existed and the error went nowhere at all.
         *
         * What that cost: a manifest Shaka could not load left the element
         * exactly as it found it, which reports as
         * `ready nothing · net empty · buffered none` with `error === null`.
         * That is the documented signature for a TORN-DOWN element (the
         * StrictMode `removeAttribute("src")` race, fixed in `f7303c0`), so
         * the one diagnostic string a wall can send pointed at a bug that was
         * already fixed and said nothing about DRM, a 404 manifest, or CORS.
         * Two different faults, one signature, and the wrong one named.
         *
         * A swallowed rejection here is never harmless: this is the only place
         * that knows why a catalogue title did not start.
         */
        clearTimeout(watchdog);
        if (disposed) return;
        const reason = describeShakaFailure(err);
        // Out, on the console, same as the key-system line above it - a
        // manifest that never loads is exactly the case this file's own
        // history (see above) says must never go silent again.
        //
        // `reason` is a `ShakaFailure` object, not a string (see
        // `describeShakaFailure`'s return type) - a first version of this
        // line interpolated the object directly and printed `[object
        // Object]` on real hardware (hasselbacken/301, 2026-09-08), which is
        // exactly the kind of swallowing this line exists to end.
        console.warn(
          `[signage] load failed: ${reason.text} (shaka ${reason.category ?? "?"}.${reason.code ?? "?"})`
        );
        // Shaka's own error objects carry a `data` array for exactly this
        // situation - for 6001 specifically, the key systems it tried and why
        // each was refused. `describeShakaFailure` intentionally reduces this
        // to one sentence for the wall; this is that detail, capped and only
        // ever field names/short codes on this error class (never a URL or
        // token), for the one case a sentence was not enough.
        try {
          const data = (err as { data?: unknown })?.data;
          if (data !== undefined) {
            console.warn(
              `[signage] load failed detail: ${JSON.stringify(data).slice(0, 400)}`
            );
          }
        } catch {
          // A diagnostic must never be the reason the real error is lost.
        }
        /*
         * THE NATIVE FALLBACK (PLAYER-6, hasselbacken/301, 2026-09-08).
         *
         * Category 6 is Shaka's own "protected and the licence step failed"
         * family - the one this panel's Android WebView build cannot itself
         * negotiate for certain live DASH content, confirmed on real
         * hardware with a `drm.servers` config already proven correct (see
         * the `[signage] configuring drm:` line above). `licenceUrl` present
         * means there IS a licence to hand a native player, as opposed to a
         * genuinely unlicensable title. `bridge.startNativePlayback` is a
         * no-op returning false everywhere this does not apply (Tizen, web,
         * an older APK) - this can only ever help, never make a working
         * platform worse.
         *
         * Fire-and-wait, not fire-and-forget: a `true` return means only
         * that native playback was ASKED for, so the ORIGINAL Shaka error is
         * withheld until native's own verdict arrives - reporting it now
         * would flash the "no licence server" banner for the ~1s native
         * needs to negotiate its own session, on the one path that is about
         * to succeed.
         */
        if (reason.category === 6 && licenceUrl) {
          const onNativeReady = () => {
            if (disposed) return;
            console.warn("[signage] native fallback playback ready");
            onKeySystem("native-exoplayer");
            onError(null);
          };
          const onNativeError = (e: Event) => {
            if (disposed) return;
            const message = (e as CustomEvent<{ message?: string }>).detail
              ?.message;
            console.warn(
              `[signage] native fallback also failed: ${message ?? "unknown"}`
            );
            // The native attempt is the one worth reporting now - it is the
            // more specific, more recent failure, on the path that was
            // supposed to be the fix.
            onError({
              code: null,
              category: null,
              text: `native playback failed: ${message ?? "unknown"}`,
            });
          };
          window.addEventListener("foyer:native-playback-ready", onNativeReady);
          window.addEventListener("foyer:native-playback-error", onNativeError);
          removeNativeListeners = () => {
            window.removeEventListener(
              "foyer:native-playback-ready",
              onNativeReady
            );
            window.removeEventListener(
              "foyer:native-playback-error",
              onNativeError
            );
          };
          const started = bridge.startNativePlayback(url, licenceUrl);
          if (started) {
            console.warn("[signage] falling back to native DASH playback");
            return;
          }
          // Not available on this shell - remove the listeners we just
          // armed and fall through to reporting Shaka's own error, unchanged
          // from before this fallback existed.
          removeNativeListeners();
          removeNativeListeners = null;
        }
        onError(reason);
      });

    return () => {
      disposed = true;
      clearTimeout(watchdog);
      // Handing the decoder back matters more here than anywhere: an attached
      // player holds buffers a bare element does not, and these panels have a
      // few hundred megabytes of decoder to share across the whole loop.
      try {
        removeManifestFilter?.();
      } catch {
        /* cleanup must never throw */
      }
      try {
        void player?.destroy();
      } catch {
        /* cleanup must never throw */
      }
      removeNativeListeners?.();
      // Idempotent and safe to call unconditionally: a no-op shell or a
      // native attempt that never started just returns false, same as
      // every other "turn this off" bridge method.
      try {
        bridge.stopNativePlayback();
      } catch {
        /* cleanup must never throw */
      }
    };
    // Both licence URLs participate: a re-resolved token is a NEW url, and a
    // player still holding the old one would keep failing its challenges
    // silently.
    // `onError` and `onKeySystem` are deliberately NOT dependencies: both are
    // re-created on every render of the layer, and including them would tear
    // the player down and rebuild it once per second - which on these panels is
    // the decoder churn that made the old watchdog worse than the stall it
    // chased.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, url, enabled, licenceUrl, playreadyLicenceUrl, attempt]);
}

function Black({ children }: { children?: React.ReactNode }) {
  return (
    <div
      style={{ position: "fixed", inset: 0, background: "#000" }}
      data-testid="signage-idle"
    >
      {children}
    </div>
  );
}

/**
 * Has the timeline this screen holds simply expired?
 *
 * True when it HAD content and every interval of it is now in the past. That
 * is a delivery failure - the panel is still rendering, so it is not dead; it
 * just has not been handed a newer schedule - and it must not be reported as
 * "nothing scheduled", which sends someone to fix a schedule that is fine.
 *
 * A timeline with no content at all is NOT this: that is a screen genuinely
 * without a schedule, and it keeps the original wording.
 */
function ranOutOfSchedule(
  timeline: SignageTimeline,
  now: Date | undefined
): boolean {
  if (!now || timeline.content.length === 0) return false;
  const t = now.getTime();
  return timeline.content.every((c) => {
    const ends = Date.parse(c.endsAt);
    return Number.isFinite(ends) && ends <= t;
  });
}

/**
 * Is this a manifest rather than a media file?
 *
 * The distinction runs through everything downstream: a manifest is a playlist
 * of segments, so it is never cached (a local `.mpd` whose segments are still
 * remote buys nothing) and it needs a player rather than a `src`.
 */
export function isManifestUrl(url: string): boolean {
  return /\.(mpd|m3u8)(\?|$)/i.test(url);
}

/**
 * Does this item need Shaka rather than the native player?
 *
 * ⭐ **Provenance, not the file extension.** Ruled by Sigge 2026-08-31:
 * "everything the customer uploads as signage material = progressive file
 * which goes to AVPlay. Everything Nenda and the content owners upload becomes
 * abr + smil and is played by shaka."
 *
 * The resolver now carries that answer on the item, because it fetched the
 * asset and already knew. `protocol` present at all means it came from the
 * catalogue and is packaged ABR; absent means a media-library upload, which is
 * progressive by definition.
 *
 * ⚠️ The URL check is kept as a FALLBACK, and only as one. It is what this
 * decided by until now, and it is wrong in a way that black-screens a wall:
 * Nenda's packaging emits a `playlist.smil`, so an ABR manifest whose url does
 * not happen to end `.mpd` or `.m3u8` was handed to the NATIVE player - the
 * exact failure a Samsung reported on 2026-08-30, reached from the other side.
 * It stays because a panel can outlive a deploy and meet a timeline resolved
 * before `protocol` existed; guessing from the url is better than assuming
 * progressive, which is how that black screen happened.
 */
export function isAbrItem(
  item: { protocol?: "hls" | "dash" },
  url: string
): boolean {
  if (item.protocol) return true;
  return isManifestUrl(url);
}

// `iframeSandbox` moved to its own module so `TemplateSlide` (which the CMS
// bundle imports) can use it without dragging this whole surface along.
export { iframeSandbox };

/**
 * The on-panel copy of this item, if there is one (TIZEN-9).
 *
 * Null when there is no copy, when caching is unavailable, when this is a
 * manifest, or when this is not a panel at all. Callers use the null to decide
 * whether a playback failure means "the cache is stale" or "the content is
 * broken" - two problems with opposite responses.
 */
function playbackCacheHit(item: SignageItem): string | null {
  // A web page is not a file, so there is nothing to have cached and nothing
  // to look up. Asking anyway would hand `getLocalUri("")` an empty key.
  if (item.kind === "url") return null;
  return isManifestUrl(item.assetUrl) ? null : getLocalUri(item.assetUrl);
}

/**
 * What to hand a player for this item: the local copy where one exists,
 * otherwise the remote URL. The local URI goes through VERBATIM.
 */
function playbackSrc(item: SignageItem): string {
  // `webUrl` rather than `assetUrl` for a page: the two are deliberately
  // separate fields, because only one of them names a file this fleet owns.
  if (item.kind === "url") return item.webUrl ?? "";
  return playbackCacheHit(item) ?? item.assetUrl;
}

/**
 * The item at `index`, or the next one that has not failed to load.
 *
 * Skipping forward rather than stopping is what keeps a screen alive when one
 * asset 404s - the loop shrinks by one instead of parking on a broken frame.
 */
export function nextPlayable(
  items: SignageItem[],
  index: number,
  dead: Set<string>
): SignageItem | null {
  return pickPlayable(items, index + 1, dead);
}

/**
 * Same walk as `pickPlayable`, but the INDEX it landed on rather than the
 * item - needed by anything that has to keep advancing from where THIS
 * answer left off (see `advancePastNaturalEnd`'s use in the surface, which
 * cannot key off an item id alone once natural-end advancement is tracking
 * a position rather than re-deriving one from the wall clock every time).
 */
export function pickPlayableIndex(
  items: SignageItem[],
  index: number,
  dead: Set<string>
): number | null {
  if (items.length === 0) return null;
  for (let i = 0; i < items.length; i += 1) {
    const candidate = (index + i) % items.length;
    if (!dead.has(items[candidate]!.id)) return candidate;
  }
  return null; // every item is dead - better black than a broken frame
}

export function pickPlayable(
  items: SignageItem[],
  index: number,
  dead: Set<string>
): SignageItem | null {
  const found = pickPlayableIndex(items, index, dead);
  return found === null ? null : items[found]!;
}

/* -------------------------------------------------------------------------- */
/* Diagnostics - preview only.                                                */
/* -------------------------------------------------------------------------- */

type Head = ReturnType<typeof playheadAt>;

/**
 * The sentence an operator needs.
 *
 * Ordered by what they can act on: a credential problem is not a scheduling
 * problem, and "nothing is scheduled" is not "the file is broken". Exported so
 * the wording is testable without a DOM.
 */
export function explainEmpty(args: {
  status: PollStatus;
  timeline: SignageTimeline | null;
  head: Head;
  deadCount: number;
  /** The clock the playhead was resolved against - server-corrected. */
  now?: Date;
}): { headline: string; detail: string } {
  const { status, timeline, head, deadCount, now } = args;
  if (status.state === "http") {
    if (status.code === 401 || status.code === 403) {
      return {
        headline: "Not authorised for this screen",
        detail: `The schedule request was refused (${status.code}). Sign in to the CMS again, or check the screen belongs to this location.`,
      };
    }
    if (status.code === 404) {
      return {
        headline: "Screen not found",
        detail:
          "No screen with this id in this location. It may have been removed or re-paired under a new id.",
      };
    }
    return {
      headline: "The server could not answer",
      detail: status.detail
        ? `${status.detail} (HTTP ${status.code})`
        : `The schedule request failed (${status.code}).`,
    };
  }
  if (status.state === "offline" && !timeline) {
    return {
      headline: "Cannot reach the server",
      detail: "No schedule has been loaded yet. Retrying every 30 seconds.",
    };
  }
  if (status.state === "loading") {
    return { headline: "Loading schedule…", detail: "" };
  }
  if (timeline && !head && ranOutOfSchedule(timeline, now)) {
    // A DIFFERENT problem wearing the same words. The screen is not
    // unscheduled - it is holding a timeline whose last interval has already
    // ended, because polls stopped landing and nobody delivered the next one.
    // Telling an operator "nothing is scheduled" sends them to rebuild a
    // schedule that was correct all along; the fault is delivery.
    return {
      headline: "The schedule on this screen has run out",
      detail:
        "The last timeline it received has ended and no newer one has arrived. The screen is reachable enough to render, so this is a polling or connectivity problem rather than a scheduling one.",
    };
  }
  if (!timeline || !head) {
    return {
      headline: "Nothing scheduled right now",
      detail:
        "A playlist only plays once it is scheduled: build a schedule from it, then assign that schedule to this weekday for this screen under Plan content.",
    };
  }
  if (head.playlist.items.length === 0) {
    /*
     * A THIRD problem wearing the same words, and the most misleading of them.
     *
     * A playlist arrives here with no items for two completely different
     * reasons: nobody put anything in it, or every title in it failed to
     * resolve upstream and was dropped on the way. The panel cannot tell them
     * apart by looking at the list - by the time it arrives, both are empty.
     *
     * The warnings can. `content_unavailable` means the resolver HAD titles
     * and could not turn them into anything playable, which is a catalogue
     * fault and nothing an operator can fix in the CMS. Telling them their
     * playlist is empty sends them to fill a playlist that is already full -
     * observed on a 355-title channel while the Content API was refusing us.
     */
    const unresolved = (timeline?.warnings ?? []).filter(
      (w) => w.code === "content_unavailable"
    ).length;
    if (unresolved > 0) {
      return {
        headline: "Content could not be fetched",
        detail: `"${head.playlist.name}" is scheduled and is not empty, but ${unresolved} title${
          unresolved === 1 ? "" : "s"
        } could not be fetched from the catalogue just now, so nothing is left to play. This is an upstream fault rather than a problem with the playlist - it should recover on its own once the catalogue answers again.`,
      };
    }
    return {
      headline: "The scheduled playlist is empty",
      detail: `"${head.playlist.name}" is on now but has no items in it.`,
    };
  }
  if (deadCount > 0) {
    return {
      headline: "Every item failed to load",
      detail: `${deadCount} item${
        deadCount === 1 ? "" : "s"
      } in "${head.playlist.name}" could not be played - a missing file, or a format this panel cannot decode.`,
    };
  }
  return { headline: "Nothing to play", detail: "" };
}

function Diagnostics({
  screenId,
  timeline,
  status,
  head,
  effectiveIndex,
  playing,
  deadCount,
  recovered,
  mediaState,
  manifestReason,
  licenceRefusal,
  clockSkewSeconds,
  onBreak,
  now,
  scrubbed,
}: {
  screenId: string;
  timeline: SignageTimeline | null;
  status: PollStatus;
  head: Head;
  /** The index actually being shown - `head.index` while the wall clock is
   *  authoritative, or the locally-HELD position for an unmeasured video
   *  advancing on its own natural end. See `heldIndex` in the surface. */
  effectiveIndex: number;
  playing: SignageItem | null;
  deadCount: number;
  /** Times the stall watchdog had to rebuild the player. */
  recovered: number;
  /** The media element's own account of a stall, or null while healthy. */
  mediaState: string | null;
  /** The PLAYER's reason, as opposed to the element's account of itself. */
  manifestReason: string | null;
  /** The SERVER's reason for not handing over a licence at all. */
  licenceRefusal: string | null;
  /** Panel clock minus server clock, in seconds. Shown only when it matters. */
  clockSkewSeconds: number;
  /** Whether a campaign break owns the wall right now, per THIS client. */
  onBreak: boolean;
  now: Date;
  scrubbed: boolean;
}) {
  const facts: string[] = [`screen ${screenId}`];
  if (onBreak) facts.push("break active");
  if (head) {
    facts.push(`${head.playlist.name} · ${head.interval.source}`);
    if (playing) {
      facts.push(`item ${effectiveIndex + 1}/${head.playlist.items.length}`);
    }
    facts.push(`until ${clockOf(new Date(head.interval.endsAt))}`);
  }
  /*
   * "Next break in Xm" - answered here too, not just on the CMS overview
   * card. A campaign break fires for `breakSeconds` out of every `3600 /
   * breaksPerHour`, commonly a ~7% duty cycle, and this preview is the
   * surface an operator actually watches to confirm a promotion is live -
   * without this line, glancing at it between markers is indistinguishable
   * from the campaign not running at all, which is exactly what got
   * triaged as a bug on 2026-09-02 before this line existed.
   */
  if (timeline) {
    const nextIn = nextBreakInSeconds(timeline, now);
    if (nextIn != null) facts.push(`next break ${formatDuration(nextIn)}`);
  }
  if (deadCount > 0) facts.push(`${deadCount} failed`);
  // Only when it is large enough to have changed which item resolves. Under a
  // minute is the normal wobble of a panel that is being corrected every 30s,
  // and printing it always would train people to ignore the line.
  if (Math.abs(clockSkewSeconds) >= 60) {
    facts.push(
      `clock ${clockSkewSeconds > 0 ? "-" : "+"}${Math.abs(
        Math.round(clockSkewSeconds / 60)
      )}min (corrected)`
    );
  }
  // A panel that plays but keeps wedging is a different problem from one that
  // never starts, and only this number tells them apart.
  if (recovered > 0) facts.push(`recovered ${recovered}x`);
  if (timeline?.revision) facts.push(`rev ${timeline.revision}`);
  facts.push(
    status.state === "ok"
      ? `polled ${clockOf(new Date(status.at))}`
      : status.state === "offline"
        ? "offline"
        : status.state === "http"
          ? `HTTP ${status.code}`
          : "polling…"
  );

  const empty = playing === null;
  const { headline, detail } = explainEmpty({
    status,
    timeline,
    head,
    deadCount,
    now,
  });

  return (
    <div
      data-testid="signage-diagnostics"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        padding: empty ? "32px 40px" : "12px 40px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        // Readable over any frame without hiding it. On an empty screen it is
        // the only thing there, so it may be as loud as it likes.
        background: empty
          ? "transparent"
          : "linear-gradient(to top, rgba(0,0,0,.72), rgba(0,0,0,0))",
        color: "#fff",
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
        pointerEvents: "none",
      }}
    >
      {empty && (
        <>
          <div style={{ fontSize: 34, fontWeight: 600, letterSpacing: -0.5 }}>
            {headline}
          </div>
          {detail && (
            <div
              style={{
                fontSize: 18,
                lineHeight: 1.45,
                maxWidth: "62ch",
                color: "rgba(255,255,255,.72)",
              }}
            >
              {detail}
            </div>
          )}
        </>
      )}
      {/*
        A stall is louder than a config warning: the wall is visibly wrong
        RIGHT NOW, and this line is the only thing that says which of the three
        identical-looking stalls it is.
      */}
      {mediaState && (
        <div
          data-testid="signage-media-stall"
          style={{
            fontSize: 15,
            lineHeight: 1.4,
            maxWidth: "72ch",
            padding: "8px 12px",
            borderRadius: 8,
            alignSelf: "flex-start",
            background: "rgba(255,86,86,.18)",
            border: "1px solid rgba(255,86,86,.45)",
            color: "#ffb3b3",
            fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
          }}
        >
          video stalled — {mediaState}
        </div>
      )}
      {/*
        The player's own refusal, shown IMMEDIATELY rather than after the four
        seconds the stall detector waits.

        A manifest that will not load never moves the playhead, so the stall
        line does eventually say something - but it says it late and it says it
        in the element's vocabulary (`ready nothing · net empty`), which is the
        documented signature of a completely different fault. This line names
        the cause the moment Shaka reports it, which in the CMS preview is
        almost always the same one: a protected channel, and no screen token to
        mint a licence with.
      */}
      {/*
        The licence route declined, which is a decision rather than a fault.
        It comes first: if no licence was minted the player never ran, so
        anything the player might have said is downstream of this.

        `content_not_scheduled` is its own sentence because it is the one an
        operator can act on in ten seconds - the preview mints only for content
        this location has actually put on a wall, so a title reachable in the
        catalogue but not yet scheduled is refused on purpose.
      */}
      {licenceRefusal && (
        <div
          data-testid="signage-licence-refusal"
          style={{
            fontSize: 15,
            lineHeight: 1.4,
            maxWidth: "72ch",
            padding: "8px 12px",
            borderRadius: 8,
            alignSelf: "flex-start",
            background: "rgba(255,176,32,.16)",
            border: "1px solid rgba(255,176,32,.45)",
            color: "#ffd28a",
            fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
          }}
        >
          no licence —{" "}
          {licenceRefusal === "content_not_scheduled"
            ? "this title is not scheduled here yet, so no licence is minted for it"
            : licenceRefusal === "no_licence_server"
              ? // The asset is fine; this deployment cannot license anything.
                // Named separately because the remedy is a deployment setting,
                // and it used to arrive wearing the catalogue's name.
                "this environment has no DRM licence server configured (VERIMATRIX_*), so protected content cannot be licensed here"
              : licenceRefusal === "no_drm_content_id"
                ? "this title is encrypted but carries no DRM content id, so nothing can be licensed for it"
                : licenceRefusal === "encrypted_without_dash"
                  ? "this title is encrypted but has no DASH rendition, which is the only one a licence can be minted for"
                  : licenceRefusal}
        </div>
      )}
      {manifestReason && !mediaState && (
        <div
          data-testid="signage-manifest-error"
          style={{
            fontSize: 15,
            lineHeight: 1.4,
            maxWidth: "72ch",
            padding: "8px 12px",
            borderRadius: 8,
            alignSelf: "flex-start",
            background: "rgba(255,176,32,.16)",
            border: "1px solid rgba(255,176,32,.45)",
            color: "#ffd28a",
            fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
          }}
        >
          stream did not load — {manifestReason}
        </div>
      )}
      {/*
        Config warnings show even while something IS playing. A timeline
        resolved in the wrong timezone plays perfectly and is wrong all day;
        if it only appeared on an empty screen it would surface exactly when
        it no longer helps.
      */}
      {collapseWarnings(timeline?.warnings ?? []).map((w) => (
        <div
          key={w.code + w.value}
          data-testid="signage-warning"
          style={{
            fontSize: 15,
            lineHeight: 1.4,
            maxWidth: "62ch",
            padding: "8px 12px",
            borderRadius: 8,
            alignSelf: "flex-start",
            background: "rgba(255,196,0,.16)",
            border: "1px solid rgba(255,196,0,.42)",
            color: "#ffd977",
          }}
        >
          {w.message}
          {w.repeats > 1 && (
            <span style={{ opacity: 0.75 }}>
              {" "}
              (and {w.repeats - 1} more like it)
            </span>
          )}
        </div>
      ))}
      <div
        style={{
          fontSize: 13,
          fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
          color: "rgba(255,255,255,.55)",
        }}
      >
        {facts.join("  ·  ")}
        {scrubbed ? `  ·  showing ${clockOf(now)}` : ""}
      </div>
    </div>
  );
}

/**
 * What the media element says about itself, in one line.
 *
 * A stalled wall looks the same whatever caused it, and the causes want
 * completely different fixes: `readyState 1` with `networkState 2` is a player
 * still waiting for bytes (delivery - range requests, a slow origin, a proxy
 * that broke the stream); `readyState 4` and not advancing is a decoder that
 * has everything it needs and stopped anyway (the panel); a non-null `error`
 * names it outright. `buffered` says how much of the clip ever arrived, which
 * separates "the file is truncated" from "the connection died at 5 seconds".
 */
/**
 * Seconds of contiguous buffered media ahead of the playhead.
 *
 * The question the wedge detector needs answered is "is there anything left to
 * wait for?", and `readyState` alone cannot answer it - an element can report
 * `enough-data` from a range that no longer covers where the playhead now is.
 * Zero on an element with no ranges, which correctly reads as "still starving".
 */
export function bufferedAhead(v: HTMLVideoElement): number {
  try {
    const t = v.currentTime;
    for (let i = 0; i < v.buffered.length; i++) {
      if (v.buffered.start(i) <= t + 0.01 && v.buffered.end(i) >= t) {
        return v.buffered.end(i) - t;
      }
    }
    return 0;
  } catch {
    return 0;
  }
}

/**
 * Is this element simply not playing, as opposed to failing to?
 *
 * A genuine stall is never paused - the element is trying and failing, which
 * is the whole signature the watchdog exists to catch - so this cannot mask
 * the fault it guards. `isConnected` covers the element having left the DOM
 * entirely, which the layer pair does on every hand-over.
 */
export function isIdleElement(v: {
  paused: boolean;
  isConnected: boolean;
}): boolean {
  return v.paused || !v.isConnected;
}

/**
 * One line of what a <video> is doing, for the diagnostics that follow a
 * hand-over. Everything here is a plain property read, safe on any element.
 */
export function layerState(v: HTMLVideoElement): string {
  const err = v.error;
  return [
    `paused=${v.paused}`,
    `ended=${v.ended}`,
    `rs=${v.readyState}`,
    `net=${v.networkState}`,
    `t=${v.currentTime.toFixed(2)}`,
    `dur=${Number.isFinite(v.duration) ? v.duration.toFixed(1) : "?"}`,
    `err=${err ? err.code : "none"}`,
    `src=${v.currentSrc ? "yes" : "no"}`,
    `connected=${v.isConnected}`,
    // Wrong-aspect reports (QB24C, 2026-10-12): without the decoded size and
    // the box it is drawn into, a stretched frame cannot be told from a
    // wrongly sized element.
    `vw=${v.videoWidth}x${v.videoHeight}`,
    `box=${Math.round(v.getBoundingClientRect().width)}x${Math.round(v.getBoundingClientRect().height)}`,
  ].join(" ");
}

export function describeMedia(v: HTMLVideoElement, stuckMs: number): string {
  const READY = [
    "nothing",
    "metadata",
    "current-frame",
    "future-data",
    "enough-data",
  ];
  const NETWORK = ["empty", "idle", "loading", "no-source"];
  const ERRORS = ["", "aborted", "network", "decode", "src-not-supported"];
  const parts = [
    `stuck ${Math.round(stuckMs / 1000)}s at ${v.currentTime.toFixed(1)}s`,
    `ready ${READY[v.readyState] ?? v.readyState}`,
    `net ${NETWORK[v.networkState] ?? v.networkState}`,
  ];
  if (v.duration && Number.isFinite(v.duration)) {
    parts.push(`dur ${v.duration.toFixed(1)}s`);
  }
  try {
    if (v.buffered.length) {
      parts.push(
        `buffered to ${v.buffered.end(v.buffered.length - 1).toFixed(1)}s`
      );
    } else {
      parts.push("buffered none");
    }
  } catch {
    /* buffered can throw on an element in a bad state */
  }
  if (v.error) {
    parts.push(`ERROR ${ERRORS[v.error.code] ?? v.error.code}`);
    if (v.error.message) parts.push(v.error.message.slice(0, 120));
  }
  return parts.join(" · ");
}

/**
 * The server's own explanation of a failed request, if it gave one.
 *
 * Best-effort by design: a body that is HTML, empty or unreadable simply
 * yields nothing, and the caller falls back to the status code. Never throws -
 * this runs inside the poll's happy path.
 */
async function errorDetail(res: Response): Promise<string | undefined> {
  try {
    const body = (await res.json()) as { message?: string; error?: string };
    const text = body?.message || body?.error;
    return typeof text === "string" && text ? text.slice(0, 300) : undefined;
  } catch {
    return undefined;
  }
}

/** HH:mm in the viewer's own zone - this caption is read by the person at the
 *  laptop, not by the wall. */
function clockOf(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;
}
